/**
 * Paperbloom inline customizer (product.pb-customize).
 *
 * Draws the studio's render model on a canvas in the page, so the preview is
 * the same composite the studio replays at print size after payment. Add to
 * cart saves the design through the studio's /api/premade-cart (which stores
 * the render spec + preview and allocates the production URL), then adds the
 * line to the THEME cart with the same `_customily-*` properties production
 * reads today.
 */
// Imported by its versioned asset URL (same URL the section modulepreloads),
// because a relative specifier would drop Shopify's ?v= cache key.
const { loadFont, loadImage, preload, render, setFileOrigin, setPlateWidth, fileUrl, artworkPlateCoverage, missingAssets } = await import(window.__pbcz.renderer);

const root = document.querySelector("[data-pb-cz]");
if (root) boot(root);

/* ---------- helpers ---------- */

const el = (tag, props = {}, kids = []) => {
  const node = Object.assign(document.createElement(tag), props);
  for (const kid of [].concat(kids)) if (kid != null) node.append(kid);
  return node;
};
const clean = (s) => String(s ?? "").replace(/[​▼]/g, "").trim();
/** Template labels that say nothing to a shopper ("Text 3", "Phrase (2)", "Subtext"). */
const GENERIC_LABEL = /^((large|medium|small|big)\s+)?(text|phrase|subtext|sub\s*text|line|words?|message)\b/i;
/** Drops designer notes: "Name (Outline 1)" -> "Name", "Name (White) _ Color Change" -> "Name". */
const plainLabel = (label) => clean(label).replace(/\(.*?\)/g, "").replace(/_.*$/, "").replace(/@\w+/g, "").replace(/\s+/g, " ").trim() || clean(label);
const uuid = () =>
  crypto.randomUUID?.() ??
  "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16),
  );
const idle = (fn) => (window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 1500 }) : setTimeout(fn, 300));

/**
 * Face templates: Customily printed the shopper's head cut out of the photo.
 * The cutout runs in the browser (assets/pb-customizer-cutout.js).
 */
const FACE_CUTOUT = new Set([
  "birthday-b",
  "party-hat-selfie",
  "body-builder",
  "bros-being-bros",
  "hot-stuff-birthday",
  "slay-queen",
  "you-are-old",
  "name-face-wrap-testing",
]);

/** Production has always read the wrap colour under this key (59 of 79 recent orders). */
const WRAP_KEY = "Gift Wrap Color ▼";
const SWATCHES_FOLDED = 10;
/**
 * Brand-safe colours that top every palette up to 10 swatches. Any hex prints:
 * the render spec keeps colours as free strings and the renderer paints them.
 */
const BRAND_COLORS = [
  { hex: "#E23D3D", name: "Red" },
  { hex: "#F5892B", name: "Orange" },
  { hex: "#F6C945", name: "Yellow" },
  { hex: "#3BA55D", name: "Green" },
  { hex: "#1FA6A0", name: "Teal" },
  { hex: "#3A7BD5", name: "Blue" },
  { hex: "#8E5BD6", name: "Purple" },
  { hex: "#F06A9A", name: "Pink" },
  { hex: "#1E1B18", name: "Black" },
  { hex: "#FFFFFF", name: "White" },
];

/** 0..1 saturation, to tell a colourful choice from greys and creams. */
function saturation(hex) {
  const n = Number.parseInt(String(hex || "").replace("#", "").slice(0, 6), 16);
  if (!Number.isFinite(n)) return 0;
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  return max === 0 ? 0 : (max - min) / max;
}
/** System faces in the font library that don't belong on wrapping paper. */
const PLAIN_FONTS = /^(arial|helvetica|times|courier|verdana|georgia|tahoma|calibri|segoe|comic sans)/i;
const MODEL_CACHE = "pbcz-model:";
const MODEL_CACHE_MAX = 12;

function lum(hex) {
  const n = Number.parseInt(String(hex || "").replace("#", "").slice(0, 6), 16);
  if (!Number.isFinite(n)) return 1;
  return (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255;
}

/** White-on-white reads as empty; start on the first palette colour that shows. */
function startingTint(layer) {
  // A Product Builder design opens on the paper colour its maker chose, however light.
  if (layer.keepDefault || lum(layer.defaultTint) < 0.88) return layer.defaultTint;
  const hit = (layer.colorOptions ?? []).find((c) => lum(c.hex) < 0.75 && lum(c.hex) > 0.04);
  return hit?.hex ?? layer.defaultTint;
}

/* ---------- model loading: memory promise → localStorage → network ---------- */

function cachedModel(id) {
  try {
    const raw = localStorage.getItem(MODEL_CACHE + id);
    return raw ? normalizeModel(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function storeModel(id, text) {
  try {
    const keys = Object.keys(localStorage).filter((k) => k.startsWith(MODEL_CACHE));
    if (keys.length >= MODEL_CACHE_MAX) localStorage.removeItem(keys[0]);
    localStorage.setItem(MODEL_CACHE + id, text);
  } catch {
    /* quota or private mode: the network copy still works */
  }
}

function fetchModel(api, id) {
  const early = window.__pbcz?.models?.[id];
  const p =
    early ??
    fetch(`${api}/api/templates/${encodeURIComponent(id)}/model?side=eps`, { mode: "cors" }).then(async (r) => {
      if (!r.ok) throw new Error(`model ${r.status}`);
      return r.text();
    });
  return p.then((text) => {
    storeModel(id, text);
    return normalizeModel(JSON.parse(text));
  });
}

const slashed = (p) => (typeof p === "string" ? p.replace(/\\/g, "/") : p);

/** "weaver-birds-a794bc40-….otf" -> "Weaver Birds"; "welcome-christmas-font-by-dani-…" -> "Welcome Christmas". */
function fontNameFromFile(file) {
  const base = String(file || "").split(/[\\/]/).pop().replace(/\.(otf|ttf|woff2?)$/i, "");
  const words = base
    .replace(/-?[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i, "")
    .replace(/[-_\s]+font[-_\s]+by[-_\s].*$/i, "")
    .split(/[-_\s]+/)
    .filter(Boolean);
  return words.map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
}

/**
 * Some templates' font libraries carry Windows paths (assets\app.customily.com\…,
 * which the studio's /files refuses) and no names or "My font 1". Every font
 * path becomes a forward-slash one here, so the preview loads it and the spec
 * sent to the print render names a file that exists; names come from the file
 * when the library's own is missing or a placeholder.
 */
function normalizeModel(m) {
  if (!m) return m;
  // Placeholder names ("Font 3", "My font 1", a path) come from the file; a file named
  // only by an id gets a plain "Style N" rather than "Font N".
  const named = (o, i) => {
    const n = String(o.name || "").trim();
    if (n && !/^(my\s+)?font\s*\d*$/i.test(n) && !/[\\/]/.test(n) && !/^assets/i.test(n)) return n;
    return fontNameFromFile(o.file) || `Style ${i + 1}`;
  };
  const options = (list) => {
    const seen = new Set();
    return (list ?? [])
      .map((o) => ({ ...o, file: slashed(o.file) }))
      .filter((o) => o.file && !seen.has(o.file) && seen.add(o.file))
      .map((o, i) => ({ ...o, name: named(o, i) }));
  };
  m.fontFiles = [...new Set((m.fontFiles ?? []).map(slashed))];
  for (const l of m.layers ?? []) {
    if (l.kind !== "text") continue;
    l.font = slashed(l.font);
    if (l.fontOptions) l.fontOptions = options(l.fontOptions);
  }
  for (const f of m.fields ?? []) {
    f.defaultFont = slashed(f.defaultFont);
    f.fontOptions = options(f.fontOptions);
  }
  return m;
}

/* ---------- main ---------- */

async function boot(root) {
  const cfg = JSON.parse(root.querySelector("[data-pb-cz-config]").textContent);
  // Colours from the shopper's saved designs (newest first), for the "From your past orders" swatches.
  cfg.pastColors = [...new Set((String(cfg.pastColorsRaw || "").match(/#[0-9a-f]{6}\b/gi) ?? []).map((h) => h.toUpperCase()))];
  // Dream It (sections/pb-dream.liquid): the design is the shopper's own, named in the URL, not the product's.
  const dreamId = cfg.dream ? new URLSearchParams(location.search).get("pbdream") : null;
  if (dreamId && /^pbd-[a-z0-9]{14}$/.test(dreamId)) {
    cfg.templateId = dreamId;
    cfg.layouts = [];
    // The shopper can go back, change the pieces and save again under the same id: never open a remembered copy.
    try { localStorage.removeItem(MODEL_CACHE + dreamId); } catch { /* private mode */ }
  }
  const api = cfg.api.replace(/\/+$/, "");
  setFileOrigin(api);
  // Plate prints: a screen-sized copy of the artwork instead of the 6000 px print file (relay api/plate.js).
  setPlateWidth(Math.min(window.innerWidth, window.screen?.width || 9999) < 760 ? 1200 : 2000);

  const $ = (sel) => root.querySelector(sel);
  const stage = $("[data-pb-cz-canvas]");
  const stageBox = $("[data-pb-cz-stage]");
  const buffer = document.createElement("canvas");
  const status = $("[data-pb-cz-status]");
  const t0 = performance.now();

  const state = {
    layoutIndex: 0,
    model: null,
    models: {},
    values: {},
    fonts: {},
    colors: {},
    choices: {},
    tints: {},
    elementValues: {},
    elementFonts: {},
    elementColors: {},
    photos: {},
    elementPhotos: {},
    logos: [],
    upload: null, // { file, image, width, height, url (staged), staging: Promise }
    rights: false,
    size: null,
    qty: 1,
    zoomed: false,
  };
  if (/[?&]pbczdebug\b/.test(location.search)) window.__pbczState = state;

  const layouts = cfg.layouts.length ? cfg.layouts : [{ id: cfg.templateId, label: "", handle: cfg.handle }];
  // Start on this product's own layout (custom-name opens Straight, not Angled).
  state.layoutIndex = Math.max(0, layouts.findIndex((l) => l.handle === cfg.handle && l.id === cfg.templateId));
  const layout = () => layouts[state.layoutIndex];
  const variants = () => cfg.variants[layout().handle] ?? cfg.variants[cfg.handle] ?? [];

  /* ----- rendering: one buffered draw per frame, never a half-painted stage ----- */

  let dirty = false;
  let drawing = false;
  let firstPaint = false;
  let paintedOnce;
  const afterPaint = new Promise((resolve) => (paintedOnce = resolve));

  /** Empty photo / artwork slots: a caramel dashed outline on cream, in the brand's colours. */
  function drawGuides(canvas, model, rs, scale) {
    const x = canvas.getContext("2d");
    for (const layer of model.layers) {
      if (layer.kind !== "image" || !layer.uploadable || rs.photos?.[layer.field]) continue;
      x.save();
      x.scale(scale, scale);
      x.translate(layer.centerX, layer.centerY);
      if (layer.rotation) x.rotate((layer.rotation * Math.PI) / 180);
      // About 2px on screen whatever the slot size (a full-sheet slot is 6000 units).
      const lw = Math.max(1.5 / scale, Math.min(layer.width, layer.height * 1, 2.5 / scale / 0.012) * 0.012);
      x.fillStyle = "rgba(251, 245, 234, 0.72)";
      x.strokeStyle = "rgba(167, 93, 18, 0.85)";
      x.lineWidth = lw;
      x.setLineDash([lw * 4, lw * 3]);
      x.fillRect(-layer.width / 2, -layer.height / 2, layer.width, layer.height);
      x.strokeRect(-layer.width / 2 + lw / 2, -layer.height / 2 + lw / 2, layer.width - lw, layer.height - lw);
      x.restore();
    }
  }

  function displayScale(model) {
    const css = stageBox.clientWidth || 600;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const px = css * dpr * (state.zoomed ? 2 : 1);
    return Math.min(1, px / Math.max(model.width, model.height));
  }

  function renderState() {
    return {
      values: state.values,
      fonts: state.fonts,
      colors: state.colors,
      choices: state.choices,
      tints: state.tints,
      elementValues: state.elementValues,
      elementFonts: state.elementFonts,
      elementColors: state.elementColors,
      photos: previewPhotos(),
      elementPhotos: logoPreview() ?? state.elementPhotos,
      highlight: state.highlight,
    };
  }

  /** The renderer draws each photo's baked frame-shaped tile when there is one. */
  function previewPhotos() {
    const out = {};
    for (const [key, p] of Object.entries(state.photos)) {
      out[key] = p.baked ? { image: p.baked, zoom: 1, panX: 0, panY: 0 } : p;
    }
    return out;
  }

  /* ----- several logos (logo / upload products): each slot wears one of them ----- */
  const MAX_LOGOS = 6;
  /**
   * Which logo each slot wears, or null when there is one logo (or none). The logos are woven through the
   * grid (next column = next logo, next row starts one or two along), so they are spread evenly and the same
   * logo never sits beside itself. Slots on opposite edges of the sheet are the same printed tile and always
   * get the same logo.
   */
  function logoSlots() {
    const logos = state.logos ?? [];
    if (cfg.mode !== "upload" || logos.length < 2 || isSheet()) return null;
    const slots = state.model.layers.filter((l) => l.kind === "image" && l.uploadable);
    const indexer = (values, size) => {
      const wrap = (v) => ((v % size) + size) % size;
      const gap = (a, z) => Math.min(Math.abs(a - z), size - Math.abs(a - z));
      const stops = [];
      for (const v of values.map(wrap).sort((a, z) => a - z)) if (!stops.some((q) => gap(q, v) < 150)) stops.push(v);
      return (v) => stops.reduce((best, q, i) => (gap(q, wrap(v)) < gap(stops[best], wrap(v)) ? i : best), 0);
    };
    const col = indexer(slots.map((l) => l.centerX), state.model.width);
    const row = indexer(slots.map((l) => l.centerY), state.model.height);
    const n = logos.length;
    const step = n <= 3 ? 1 : 2;
    const out = {};
    for (const l of slots) out[l.uuid] = logos[(col(l.centerX) + step * row(l.centerY)) % n];
    return out;
  }
  function logoPreview() {
    const map = logoSlots();
    if (!map) return null;
    return Object.fromEntries(Object.entries(map).map(([id, t]) => [id, t.baked ? { image: t.baked, zoom: 1, panX: 0, panY: 0 } : t]));
  }
  /** The second, third… logos follow the first one's size, rotation, background removal and text. */
  async function bakeMoreLogos(first, layer) {
    for (const t of (state.logos ?? []).slice(1)) {
      for (const k of ["zoom", "panX", "panY", "rot", "quarter", "tiltDeg", "fit", "sheetFit"]) t[k] = first[k];
      t.text = first.text ? { ...first.text } : undefined;
      t.source ??= t.image;
      const cutKey = first.cut?.on ? `${first.cut.tol}|${first.cut.mode}` : "";
      if (t.cutKey !== cutKey) {
        t.image = cutKey ? cutout(t.source, first.cut.tol, first.cut.mode) : t.source;
        t.cutKey = cutKey;
      }
      const c = document.createElement("canvas");
      [c.width, c.height] = bakeSize(layer);
      await bakeUpload(t, layer, c);
      t.baked = c;
      t.rev = (t.rev || 0) + 1;
      queueHosting(t);
    }
  }

  const thumbCanvas = $("[data-pb-cz-thumbcanvas]");
  // Live mockups (assets/pb-live-mockups.js): the gallery scenes wear the current design.
  const liveThumbs = [...root.querySelectorAll("[data-pb-live-thumb]")];
  const liveStage = $("[data-pb-live-stage]");
  let live = false;
  const mountLive = () => {
    // Dream It's wizard (customizer hidden) drives the mockups from its own preview.
    if (document.documentElement.classList.contains("pb-dream-new")) return;
    if (!live && window.PBLive && liveThumbs.length) live = window.PBLive.mount(liveThumbs, buffer, { repeatIn: 20 });
  };
  const photo = $("[data-pb-cz-photo]");

  /** Back to the live preview (from a mockup photo) whenever the design changes. */
  function showLive() {
    // Typing while looking at a live mockup keeps the mockup; it updates as they type.
    if (stageBox.classList.contains("is-livemock")) return;
    if (!stageBox.classList.contains("is-photo")) return;
    stageBox.classList.remove("is-photo");
    for (const t of root.querySelectorAll("[data-pb-cz-thumb]")) t.classList.toggle("is-on", !t.dataset.src);
  }

  function schedule() {
    showLive();
    dirty = true;
    if (drawing) return;
    drawing = true;
    requestAnimationFrame(async () => {
      while (dirty) {
        dirty = false;
        const model = state.model;
        if (!model) break;
        const rs = renderState();
        const scale = displayScale(model);
        await render(buffer, model, rs, { scale, emptySlots: false });
        if (model !== state.model) continue;
        if (state.showGuides !== false) drawGuides(buffer, model, rs, scale);
        if (stage.width !== buffer.width) stage.width = buffer.width;
        if (stage.height !== buffer.height) stage.height = buffer.height;
        stage.getContext("2d").drawImage(buffer, 0, 0);
        const lit = state.dim && model.layers.find((l) => l.uuid === state.dim);
        if (lit) {
          // Everything else at ~35%: a paper-coloured wash, then the layer on its own.
          const g = stage.getContext("2d");
          const solo = await soloRender(lit, buffer.width / model.width);
          g.globalAlpha = 0.65;
          g.fillStyle = bgHexNow() || model.bgColor || "#FFFFFF";
          g.fillRect(0, 0, stage.width, stage.height);
          g.globalAlpha = 1;
          g.drawImage(solo, 0, 0, stage.width, stage.height);
        }
        if (thumbCanvas) {
          const t = thumbCanvas.getContext("2d");
          t.clearRect(0, 0, thumbCanvas.width, thumbCanvas.height);
          t.drawImage(buffer, 0, 0, thumbCanvas.width, thumbCanvas.height);
        }
        paintLowRes();
        if (firstPaint) {
          mountLive();
          window.PBLive?.update();
        }
        if (!firstPaint) {
          firstPaint = true;
          idle(() => paintedOnce());
          mountLive();
          stageBox.classList.add("is-live");
          const ms = Math.round(performance.now() - (window.__pbcz?.t0 ?? t0));
          root.dataset.firstPaintMs = String(ms);
          performance.mark?.("pbcz:first-paint");
        }
      }
      drawing = false;
    });
  }

  /* ----- open a template (initial + layout switch) ----- */

  async function openLayout(index, { carry = false } = {}) {
    const { id } = layouts[index];
    const cached = !state.models[id] && cachedModel(id);
    let model = state.models[id];
    const network = model ? null : fetchModel(api, id);
    if (!model && cached) model = cached;
    if (!model) model = await network;

    const prev = state.model;
    state.layoutIndex = index;
    state.models[id] = model;
    applyModel(model, prev, carry);
    const seen = new Set();
    for (const [key, p] of Object.entries(state.photos)) {
      if (seen.has(p)) continue;
      seen.add(p);
      await bake(p, key);
    }
    await preload(model, { fonts: false });
    await Promise.all(model.fields.map((f) => loadFont(state.fonts[f.key])));
    if (state.model !== model) return;
    buildControls();
    schedule();

    // A stale cached model is fine for the first paint; swap in the fresh one.
    if (network && cached) {
      network
        .then((fresh) => {
          if (JSON.stringify(fresh) === JSON.stringify(cached)) return;
          state.models[id] = fresh;
          if (state.layoutIndex === index) {
            applyModel(fresh, state.model, true);
            buildControls();
            schedule();
          }
        })
        .catch(() => {});
    }
  }

  /** Paper colour can't change without breaking the art (see scripts/customizer-lock-bg.json). */
  function bgLocked(model) {
    if ((cfg.lockBg ?? []).includes(model.id)) return true;
    // A baked plate is the harvest as-is: the renderer never paints a paper colour under it.
    return model.artworkPlateMode === "baked" && Boolean(artworkPlateCoverage(model));
  }

  function applyModel(model, prev, carry) {
    const bgPrev = prev?.layers.find((l) => l.kind === "vector" && l.isBackground);
    const bgHex = bgPrev && Object.values(state.tints[bgPrev.uuid] ?? {})[0];
    const keep = carry ? { values: state.values, fonts: state.fonts, colors: state.colors } : null;

    state.model = model;
    state.values = {};
    state.fonts = {};
    state.colors = {};
    state.choices = {};
    state.tints = {};
    state.elementValues = {};
    state.elementFonts = {};
    state.elementColors = {};
    state.elementPhotos = {};

    for (const f of model.fields) {
      const font = keep?.fonts[f.key];
      const fontOk = font && (f.fontOptions ?? []).some((o) => o.file === font);
      // A sample name makes the preview show where their words go.
      // Customily's own default words first (scripts/fetch-customizer-text-defaults.mjs).
      // A line with no words of its own still starts filled in, so no part of the design opens looking blank.
      let sample = cfg.textDefaults?.[model.id]?.[f.key] ?? model.textDefaults?.[f.key] ?? cfg.sampleText?.[f.key] ?? (/name/i.test(f.label) ? cfg.sampleText?.Name : "");
      if (!sample) sample = cfg.sampleText?.Phrase || "Just for you";
      if (sample && !f.multiline) sample = sample.replace(/\s*\n\s*/g, " ");
      state.values[f.key] = keep?.values[f.key] ?? sample ?? "";
      state.fonts[f.key] = fontOk ? font : f.defaultFont;
      // Some templates default to text the same colour as their paper
      // (scan-customizer-contrast.mjs); the map carries a readable default.
      const readable = cfg.colorDefaults?.[`${model.id}~${f.key}`];
      state.colors[f.key] = keep?.colors[f.key] ?? readable ?? f.defaultColor;
    }
    const locked = bgLocked(model);
    for (const layer of model.layers) {
      if (layer.kind !== "vector" || !layer.isBackground) continue;
      // Locked: the plate was cut on exactly this colour, so only it prints true.
      const hex = locked ? model.artworkPlateBg || layer.defaultTint : bgHex || (layer.defaultTint ? startingTint(layer) : null);
      if (hex) state.tints[layer.uuid] = { "*": hex };
    }
    // Plain name prints (just a background and text) open on a lively pink-and-white
    // pair instead of a grey one, unless the template's own colour is already bright.
    // Name prints and logo/upload tiles: just a background, text and photo slots.
    const plain = !model.artworkPlate && model.layers.every((l) => l.kind === "text" || (l.kind === "vector" && l.isBackground) || (l.kind === "image" && l.uploadable));
    const bgPlain = model.layers.find((l) => l.kind === "vector" && l.isBackground);
    // A layout switch keeps their colour; coming from one with no paper colour (full sheet) is a fresh start.
    const fresh = !carry || !prev?.layers?.some((l) => l.kind === "vector" && l.isBackground);
    if (fresh && plain && bgPlain && saturation(Object.values(state.tints[bgPlain.uuid] ?? {})[0]) < 0.25) {
      state.tints[bgPlain.uuid] = { "*": "#F06A9A" };
      for (const f of model.fields) {
        if (!cfg.colorDefaults?.[`${model.id}~${f.key}`]) state.colors[f.key] = "#FFFFFF";
      }
    }
    state.original = { fonts: { ...state.fonts }, colors: { ...state.colors } };
    const bgL = model.layers.find((l) => l.kind === "vector" && l.isBackground);
    state.originalBg = bgL ? Object.values(state.tints[bgL.uuid] ?? {})[0] : null;

    // Photos follow their field across layouts; an upload product's one
    // artwork fills every slot.
    const prevPhotos = state.photos;
    const shared = cfg.mode === "upload" ? Object.values(prevPhotos)[0] : null;
    state.photos = {};
    for (const f of model.photos ?? []) {
      const p = prevPhotos[f.key] ?? shared;
      if (p) state.photos[f.key] = p;
    }
  }

  /* ----- controls ----- */

  const steps = $("[data-pb-cz-steps]");

  /**
   * What the shopper sees for a text field: proper template labels as they are,
   * generic ones replaced by what they typed ("To Steeve"), else "Line 3".
   * Display only: order properties keep the template's own labels.
   */
  function shownLabel(f) {
    const label = plainLabel(f.label);
    if (!GENERIC_LABEL.test(label)) return label;
    const typed = clean(state.values[f.key]);
    if (typed) return typed.length > 22 ? `${typed.slice(0, 21)}…` : typed;
    return `Line ${state.model.fields.indexOf(f) + 1}`;
  }
  /** Short role label for an input: the template's own label when it means something, else "Line N". */
  function roleLabel(f) {
    const label = plainLabel(f.label);
    return GENERIC_LABEL.test(label) ? `Line ${state.model.fields.indexOf(f) + 1}` : label;
  }

  const labelNode = (f, suffix = "") => {
    const n = el("span", { textContent: shownLabel(f) + suffix });
    n.dataset.labelFor = f.key;
    n.dataset.labelSuffix = suffix;
    return n;
  };
  function refreshLabels(f) {
    for (const n of root.querySelectorAll(`[data-label-for="${CSS.escape(f.key)}"]`)) n.textContent = shownLabel(f) + (n.dataset.labelSuffix || "");
  }
  let stepNo = 0;

  /** "1 · Their name" header with a live value on the right (counter, font, colours…). */
  function step(title, meta, body) {
    stepNo += 1;
    const metaEl = typeof meta === "string" ? el("span", { className: "pbcz-step__meta", textContent: meta }) : meta;
    const head = el("div", { className: "pbcz-step__h" }, [el("b", { textContent: `${stepNo} · ${title}` }), metaEl]);
    return el("section", { className: "pbcz-step" }, [head, body]);
  }

  /**
   * The template's palette first, topped up to 10 with brand colours, a
   * "+N more" fold for long palettes, and a Custom swatch (native picker).
   */
  /**
   * Ten bright primaries (the template's own colour first when it's colourful),
   * any custom colours this shopper added, and the wheel. A colour picked on
   * the wheel becomes a new selected swatch; the wheel stays for more.
   */
  function swatches(options, getSelected, onPick, { label, key, signature } = {}) {
    const wrap = el("div", { className: "pbcz-swatches", role: "radiogroup" });
    if (label) wrap.setAttribute("aria-label", label);
    // Signed in: colours from their own saved designs and orders (newest first), above ours.
    const past = (cfg.pastColors ?? []).slice(0, 8);
    const pastRow = past.length ? el("div", { className: "pbcz-swatches", role: "radiogroup" }) : null;
    pastRow?.setAttribute("aria-label", "Colors from your past orders");
    const set = el("div", { className: "pbcz-swatchset" }, pastRow
      ? [el("span", { className: "pbcz-swatchset__cap", textContent: "From your past orders" }), pastRow, el("span", { className: "pbcz-swatchset__cap", textContent: "Our colors" }), wrap]
      : [wrap]);
    const swatchButton = (o, current, after) => {
      const on = o.hex.toLowerCase() === current;
      const text = colorText(options, o.hex);
      const b = el("button", {
        type: "button",
        className: `pbcz-swatch${on ? " is-on" : ""}${o.custom ? " is-custom" : ""}`,
        title: text,
        style: `--sw:${o.hex}`,
      });
      b.setAttribute("role", "radio");
      b.setAttribute("aria-checked", String(on));
      b.setAttribute("aria-label", text);
      b.addEventListener("click", () => {
        onPick({ hex: o.hex, name: o.name || named(o.hex) || "" });
        after();
      });
      return b;
    };
    const listKey = key || label || "colors";
    state.customSwatches ??= {};
    const customs = (state.customSwatches[listKey] ??= []);
    const named = (hex) => options.find((o) => o.hex && o.hex.toLowerCase() === hex.toLowerCase())?.name;
    const build = () => {
      const current = String(getSelected() || "").toLowerCase();
      const seen = new Set();
      const list = [];
      const push = (hex, name, custom = false) => {
        const h = String(hex || "").toLowerCase();
        if (!/^#[0-9a-f]{6}$/.test(h) || seen.has(h)) return;
        seen.add(h);
        list.push({ hex, name: name || named(hex) || "", custom });
      };
      if (signature && saturation(signature) > 0.3) push(signature, named(signature));
      for (const c of BRAND_COLORS) {
        if (list.length >= SWATCHES_FOLDED) break;
        push(c.hex, c.name);
      }
      for (const c of customs) push(c, "", true);
      // A colour chosen elsewhere (a style, a pair, the template) still shows as picked.
      if (current && !seen.has(current)) push(current, named(current), true);
      wrap.replaceChildren();
      pastRow?.replaceChildren(...past.map((hex) => swatchButton({ hex, name: named(hex) || "" }, current, build)));
      for (const o of list) wrap.append(swatchButton(o, current, build));
      const picker = el("input", { type: "color", value: /^#[0-9a-f]{6}$/i.test(current) ? current : "#f06a9a" });
      const wheel = el("label", { className: "pbcz-swatch pbcz-swatch--custom", title: "More colors" }, [picker]);
      wheel.setAttribute("aria-label", "More colors");
      picker.addEventListener("change", () => {
        const hex = picker.value.toUpperCase();
        if (!customs.some((c) => c.toLowerCase() === hex.toLowerCase())) customs.push(hex);
        onPick({ hex, name: "" });
        build();
      });
      picker.addEventListener("input", () => onPick({ hex: picker.value.toUpperCase(), name: "" }));
      wrap.append(wheel);
    };
    build();
    return set;
  }

  /** "Forest Green · #1F4F3A", or just the code for a colour with no name, so shoppers always see the hex. */
  function colorText(options, hex) {
    if (!hex) return "";
    const code = String(hex).toUpperCase();
    const name = nameOf(options, hex);
    return String(name).toUpperCase() === code ? code : `${name} · ${code}`;
  }

  function nameOf(options, hex) {
    const h = String(hex).toLowerCase();
    return (
      options?.find((o) => o.hex && o.hex.toLowerCase() === h)?.name ||
      BRAND_COLORS.find((c) => c.hex.toLowerCase() === h)?.name ||
      String(hex).toUpperCase()
    );
  }

  /** "grinchie-regular-e564eda1-….otf" -> "Grinchie Regular" when a library entry has no name. */
  const nameFromFile = (file) => fontNameFromFile(file) || "Custom font";

  let fontCatalog = null;
  /** Every font the studio can print (/api/fonts), plain system faces left out. */
  function loadFontCatalog() {
    fontCatalog ??= fetch(`${api}/api/fonts`)
      .then((r) => r.json())
      .then((data) => {
        const seen = new Set();
        const out = [];
        for (const f of data.fonts ?? []) {
          // The library reports Windows-style paths; /files wants forward slashes.
          const file = String(f.file || "").replace(/\\/g, "/");
          if (!/\.(otf|ttf|woff2?)$/i.test(file) || PLAIN_FONTS.test(f.name || "")) continue;
          const name = `${f.name || nameFromFile(file)}${f.style && !/^regular$/i.test(f.style) ? ` ${f.style}` : ""}`.trim();
          if (seen.has(name.toLowerCase())) continue;
          seen.add(name.toLowerCase());
          out.push({ file, name, usedBy: f.usedBy || 0 });
        }
        return out.sort((a, b) => b.usedBy - a.usedBy || a.name.localeCompare(b.name));
      })
      .catch(() => []);
    return fontCatalog;
  }

  function fontLabel(field, file) {
    const own = (field.fontOptions ?? []).find((o) => o.file === file);
    return own?.name || state.fontNames?.[file] || nameFromFile(file);
  }

  function fontName(field) {
    return fontLabel(field, state.fonts[field.key]);
  }

  /** Rough style family from a font's name, to suggest fonts that suit the template. */
  function fontStyle(name) {
    const n = String(name).toLowerCase();
    if (/script|hand|brush|sign|callig|swash|vibes|dream|moon|sweet|hello|love|augustine|pearberry|hatton|signature|monoline|cursive|marker/.test(n)) return "script";
    if (/serif|roman|garamond|playfair|bodoni|didot|baskerville|caslon|times/.test(n) && !/sans/.test(n)) return "serif";
    if (/sans|grotesk|montserrat|futura|gothic|helvet|poppins|lato|open|roboto|inter|nunito/.test(n)) return "sans";
    return "display";
  }

  const SUGGESTED = 5;
  /**
   * Chip and "More fonts" faces are previews, not the design: they wait for the
   * first paint, fetch at low priority (warming the cache), then load.
   */
  function previewFont(file) {
    return afterPaint
      .then(() => fetch(fileUrl(file), { priority: "low", mode: "cors", credentials: "omit" }).catch(() => null))
      .then(() => loadFont(file));
  }
  const OWN_SHOWN = 12; // in a line's font drawer

  /**
   * Five fonts per field: the template's own first, then printable fonts that
   * suit its style (same family first, then one of each other family).
   */
  /**
   * Font chips for a field: the current font first, then up to `max` in all
   * (5 in the Font step, 12 in a line's drawer); the rest lead "More fonts",
   * so their files only download once that opens.
   */
  function fontChips(field, onPick, { max = SUGGESTED } = {}) {
    const wrap = el("div", { className: "pbcz-fonts", role: "radiogroup" });
    wrap.setAttribute("aria-label", `${clean(field.label)} font`);
    const allOwn = (field.fontOptions?.length ? field.fontOptions : [{ file: field.defaultFont }]).map((o) => ({
      ...o,
      name: o.name || nameFromFile(o.file),
    }));
    // Long libraries: the current font plus the next 11; the rest lead "More fonts".
    const cur = state.fonts[field.key];
    const ordered = [...allOwn.filter((o) => o.file === cur), ...allOwn.filter((o) => o.file !== cur)];
    const opts = ordered.slice(0, max);
    field.moreOwn = ordered.slice(max);
    let visible = false;

    const chip = (o) => {
      const on = state.fonts[field.key] === o.file;
      const b = el("button", { type: "button", className: `pbcz-font${on ? " is-on" : ""}` }, [
        el("span", { className: "pbcz-font__sample", textContent: sampleFor(field) }),
        el("span", { className: "pbcz-font__name", textContent: o.name }),
      ]);
      b.setAttribute("role", "radio");
      b.setAttribute("aria-checked", String(on));
      b.dataset.file = o.file;
      b.addEventListener("click", async () => {
        state.fonts[field.key] = o.file;
        for (const x of wrap.closest(".pbcz-fontgroup")?.querySelectorAll(".pbcz-font") ?? wrap.children) {
          x.classList.toggle("is-on", x === b);
          x.setAttribute("aria-checked", String(x === b));
        }
        onPick?.(o);
        await loadFont(o.file);
        schedule();
      });
      if (visible) visible.observe(b);
      return b;
    };
    // A face that won't load would show as the fallback "Aa": drop it (unless it's the current one).
    const face = (b) =>
      previewFont(b.dataset.file).then((family) => {
        if (family) b.firstChild.style.fontFamily = `"${family}", serif`;
        else if (state.fonts[field.key] !== b.dataset.file) b.remove();
      });

    for (const o of opts) wrap.append(chip(o));

    if (opts.length < SUGGESTED) {
      loadFontCatalog().then((all) => {
        const own = new Set(opts.map((o) => o.file));
        const style = fontStyle(opts[0]?.name);
        const pool = all.filter((f) => !own.has(f.file));
        const picks = [];
        const take = (f) => f && !picks.includes(f) && picks.push(f);
        // Same family first (most-used), then one from each other family.
        for (const f of pool.filter((x) => fontStyle(x.name) === style)) {
          if (picks.length >= Math.ceil((SUGGESTED - opts.length) / 2)) break;
          take(f);
        }
        for (const fam of ["script", "serif", "display", "sans"]) {
          if (picks.length >= SUGGESTED - opts.length) break;
          if (fam !== style) take(pool.find((x) => fontStyle(x.name) === fam && !picks.includes(x)));
        }
        for (const f of pool) {
          if (picks.length >= SUGGESTED - opts.length) break;
          take(f);
        }
        state.fontNames ??= {};
        for (const f of picks) {
          state.fontNames[f.file] = f.name;
          wrap.append(chip(f));
        }
      });
    }

    // Each chip's face loads when that chip scrolls near the viewport.
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          io.unobserve(e.target);
          face(e.target);
        }
      },
      { rootMargin: "100px" },
    );
    visible = io;
    for (const b of wrap.children) io.observe(b);
    return wrap;
  }

  /**
   * "More fonts": the next 12 printable fonts at a time, each shown in its own
   * face, with a small search for when they know what they want.
   */
  function moreFonts(field, onPick) {
    const PAGE = 12;
    const box = el("div", { className: "pbcz-morefonts" });
    const toggle = el("button", { type: "button", className: "pbcz-pill pbcz-pill--out pbcz-pill--sm" }, [
      el("span", { textContent: "More fonts" }),
    ]);
    const panel = el("div", { className: "pbcz-fontpanel", hidden: true });
    const grid = el("div", { className: "pbcz-fonts pbcz-fonts--all", role: "radiogroup" });
    const next = el("button", { type: "button", className: "pbcz-pill pbcz-pill--out pbcz-pill--sm" }, [el("span", { textContent: "More" })]);
    const search = el("input", { type: "search", className: "pbcz-input pbcz-input--sm pbcz-fontsearch", placeholder: "Search fonts" });
    search.setAttribute("aria-label", "Search fonts");
    panel.append(grid, el("div", { className: "pbcz-fontpanel__foot" }, [next, search]));
    box.append(toggle, panel);
    let all = null;
    let shown = 0;
    let io = null;

    const chip = (f) => {
      const on = state.fonts[field.key] === f.file;
      const b = el("button", { type: "button", className: `pbcz-font${on ? " is-on" : ""}` }, [
        el("span", { className: "pbcz-font__sample", textContent: sampleFor(field) }),
        el("span", { className: "pbcz-font__name", textContent: f.name }),
      ]);
      b.dataset.file = f.file;
      b.setAttribute("role", "radio");
      b.setAttribute("aria-checked", String(on));
      b.addEventListener("click", async () => {
        state.fonts[field.key] = f.file;
        for (const x of box.closest(".pbcz-fontgroup")?.querySelectorAll(".pbcz-font") ?? []) {
          x.classList.toggle("is-on", x === b);
          x.setAttribute("aria-checked", String(x === b));
        }
        onPick?.(f);
        await loadFont(f.file);
        schedule();
      });
      io.observe(b);
      return b;
    };
    const more = () => {
      const page = all.slice(shown, shown + PAGE);
      shown += page.length;
      grid.append(...page.map(chip));
      next.hidden = shown >= all.length;
    };
    const open = async () => {
      if (all) return;
      grid.append(el("p", { className: "pbcz-hint", textContent: "Loading fonts…" }));
      const shownFiles = new Set([...(box.closest(".pbcz-fontgroup")?.querySelectorAll(".pbcz-font") ?? [])].map((b) => b.dataset.file));
      const own = (field.moreOwn ?? []).map((o) => ({ file: o.file, name: o.name || nameFromFile(o.file) }));
      const ownFiles = new Set(own.map((o) => o.file));
      all = [...own, ...(await loadFontCatalog()).filter((f) => !ownFiles.has(f.file))].filter((f) => !shownFiles.has(f.file));
      state.fontNames ??= {};
      for (const f of all) state.fontNames[f.file] = f.name;
      grid.replaceChildren();
      io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (!e.isIntersecting) continue;
            io.unobserve(e.target);
            previewFont(e.target.dataset.file).then((family) => {
              if (family) e.target.firstChild.style.fontFamily = `"${family}", serif`;
              else if (state.fonts[field.key] !== e.target.dataset.file) e.target.remove();
            });
          }
        },
        { rootMargin: "120px" },
      );
      more();
    };
    toggle.addEventListener("click", async () => {
      panel.hidden = !panel.hidden;
      toggle.firstChild.textContent = panel.hidden ? "More fonts" : "Fewer fonts";
      if (!panel.hidden) await open();
    });
    next.addEventListener("click", more);
    search.addEventListener("input", () => {
      const q = search.value.trim().toLowerCase();
      if (!all) return;
      grid.replaceChildren();
      if (!q) {
        shown = 0;
        more();
        return;
      }
      const hits = all.filter((f) => f.name.toLowerCase().includes(q)).slice(0, 24);
      grid.append(...hits.map(chip));
      next.hidden = true;
    });
    return box;
  }

  function sampleFor(field) {
    const v = clean(state.values[field.key]);
    return v ? v.slice(0, 14) : "Aa";
  }

  /**
   * Lines the shopper sees as one: fields with the same default words (a
   * phrase repeated across the sheet) are driven by one input, until they
   * choose "Edit separately".
   */
  function fieldGroups() {
    const m = state.model;
    const defs = cfg.textDefaults?.[m.id] ?? {};
    state.unlinked ??= new Set();
    const norm = (t) => String(t || "").replace(/\s+/g, " ").trim().toLowerCase();
    const groups = [];
    const byText = new Map();
    for (const f of m.fields) {
      const t = norm(defs[f.key] ?? state.values[f.key]);
      const key = t ? `t:${t}` : null;
      if (key && !state.unlinked.has(key) && byText.has(key)) byText.get(key).fields.push(f);
      else {
        const g = { key: key || `f:${f.key}`, fields: [f] };
        groups.push(g);
        if (key) byText.set(key, g);
      }
    }
    return groups;
  }

  /** Inline per-line style: an Aa font button and a colour dot, opening under the input. */
  /* ----- inline drawers: one open at a time, fade/slide in place ----- */

  let activeDrawer = null;
  function toggleDrawer(d, fill) {
    if (activeDrawer === d) return closeDrawer();
    closeDrawer(true);
    d.panel.replaceChildren();
    fill(d.panel);
    d.panel.hidden = false;
    requestAnimationFrame(() => d.panel.classList.add("is-open"));
    activeDrawer = d;
    d.onOpen?.();
  }
  function closeDrawer(instant = false) {
    const d = activeDrawer;
    if (!d) return;
    activeDrawer = null;
    d.panel.classList.remove("is-open");
    d.onClose?.();
    const done = () => {
      if (activeDrawer === d) return;
      d.panel.hidden = true;
      d.panel.replaceChildren();
    };
    if (instant) done();
    else setTimeout(done, 180);
  }
  /** A pick from the colour wheel keeps the drawer open while they drag. */
  const closeAfterPick = () => {
    if (document.activeElement?.type !== "color") closeDrawer();
  };

  const syncLines = () => lineSyncs.forEach((sync) => sync());

  function applyAllBox() {
    const cb = el("input", { type: "checkbox" });
    const row = el("label", { className: "pbcz-applyall" }, [cb, el("span", { textContent: "Apply to all lines" })]);
    if (state.model.fields.length < 2) row.style.display = "none"; // nothing else to apply it to
    return [cb, row];
  }

  /** The Aa button and colour dot on a line; each opens its drawer right under the line. */
  function lineStyle(group, panel, line) {
    const f = group.fields[0];
    const glyph = el("span", { className: "pbcz-line__glyph", textContent: "Aa" });
    const aa = el("button", { type: "button", className: "pbcz-line__aa" }, [glyph]);
    const dot = el("button", { type: "button", className: "pbcz-line__dot" });
    aa.setAttribute("aria-label", `Font for ${shownLabel(f)}`);
    dot.setAttribute("aria-label", `Color for ${shownLabel(f)}`);
    aa.setAttribute("aria-expanded", "false");
    dot.setAttribute("aria-expanded", "false");
    const sync = () => {
      dot.style.setProperty("--sw", state.colors[f.key] || "#000");
      dot.classList.toggle("is-light", lum(state.colors[f.key] || "#000000") > 0.8);
      aa.title = fontName(f);
      loadFont(state.fonts[f.key]).then((fam) => fam && (glyph.style.fontFamily = `"${fam}", serif`));
    };
    const mark = (what) => {
      line.classList.toggle("is-active", !!what);
      aa.setAttribute("aria-expanded", String(what === "font"));
      dot.setAttribute("aria-expanded", String(what === "color"));
    };
    const targets = (all) => (all ? state.model.fields : group.fields);
    const fontDrawer = { panel, onOpen: () => mark("font"), onClose: () => mark(null) };
    const colorDrawer = { panel, onOpen: () => mark("color"), onClose: () => mark(null) };
    aa.addEventListener("click", () =>
      toggleDrawer(fontDrawer, (p) => {
        const [all, row] = applyAllBox();
        const g = el("div", { className: "pbcz-fontgroup" });
        g.dataset.field = f.key;
        const onPick = (o) => {
          for (const x of targets(all.checked)) state.fonts[x.key] = o.file;
          syncLines();
          schedule();
          closeDrawer();
        };
        g.append(fontChips(f, onPick, { max: OWN_SHOWN }), moreFonts(f, onPick));
        p.append(g, row);
      }),
    );
    dot.addEventListener("click", () =>
      toggleDrawer(colorDrawer, (p) => {
        const [all, row] = applyAllBox();
        p.append(
          swatches(f.colorOptions ?? [], () => state.colors[f.key], (o) => {
            for (const x of targets(all.checked)) state.colors[x.key] = o.hex;
            syncLines();
            syncLineWarns();
            schedule();
            closeAfterPick();
          }, { label: `${shownLabel(f)} color`, key: f.key, signature: state.original?.colors?.[f.key] }),
          row,
        );
      }),
    );
    sync();
    lineSyncs.push(sync);
    return [aa, dot];
  }

  /* ----- colour rows: current colour at a glance, swatches one tap away ----- */

  /** Recolourable artwork layers a shopper can see (not under an artwork plate). */
  function accentLayers() {
    const plate = artworkPlateCoverage(state.model);
    return state.model.layers.filter(
      (l, i) => l.kind === "vector" && !l.isBackground && l.recolorable && l.colorOptions?.length && !(plate && i < plate.upTo),
    );
  }
  const tintOf = (layer) => Object.values(state.tints[layer.uuid] ?? {})[0] ?? layer.selectedColor ?? layer.defaultTint;
  /** "Snowflake Layer 1" -> "Snowflake 1"; a bare "2" -> "Accent 2". */
  const accentLabel = (layer, n) => {
    const t = plainLabel(layer.name).replace(/\b(layer|colou?r)\b/gi, "").replace(/\s+/g, " ").replace(/^[\s\-–]+|[\s\-–]+$/g, "").trim();
    return /[a-z]{3}/i.test(t) ? t : `Accent ${n}`;
  };

  function colorRow({ label, layer, options, get, set, key, signature }) {
    const row = el("div", { className: "pbcz-crow" });
    const icon = layer ? el("canvas", { className: "pbcz-crow__thumb", width: 80, height: 80 }) : el("span", { className: "pbcz-crow__dot" });
    const sw = el("span", { className: "pbcz-crow__sw" });
    const name = el("span", { className: "pbcz-crow__name" });
    const head = el("button", { type: "button", className: "pbcz-crow__head" }, [
      icon,
      el("span", { className: "pbcz-crow__label", textContent: label }),
      el("span", { className: "pbcz-crow__cur" }, [layer ? sw : null, name]),
      el("span", { className: "pbcz-crow__chev", "aria-hidden": "true" }),
    ]);
    head.setAttribute("aria-expanded", "false");
    const panel = el("div", { className: "pbcz-drawer", hidden: true });
    const sync = () => {
      const hex = get();
      // No tint yet: the artwork's own colour, read off its render.
      name.textContent = !hex || hex === signature ? "Original" : colorText(options, hex);
      if (layer) {
        if (hex) sw.style.background = hex;
        else soloRender(layer, 360 / Math.max(state.model.width, state.model.height)).then((c) => (sw.style.background = inkOf(c)));
        paintLayerThumb(layer, icon);
      } else icon.style.background = hex;
    };
    const drawer = {
      panel,
      onOpen: () => {
        row.classList.add("is-active");
        head.setAttribute("aria-expanded", "true");
      },
      onClose: () => {
        row.classList.remove("is-active");
        head.setAttribute("aria-expanded", "false");
      },
    };
    head.addEventListener("click", () =>
      toggleDrawer(drawer, (p) =>
        p.append(
          swatches(options, get, (o) => {
            set(o.hex);
            sync();
            schedule();
            closeAfterPick();
          }, { label, key, signature }),
        ),
      ),
    );
    if (layer) {
      head.addEventListener("mouseenter", () => spotlight(layer));
      head.addEventListener("focus", () => spotlight(layer));
    }
    sync();
    row.sync = sync;
    row.append(head, panel);
    return row;
  }

  /** Background + every accent layer, as collapsible rows. */
  function colorRows({ background = true } = {}) {
    const box = el("div", { className: "pbcz-crows" });
    const bg = background && !bgLocked(state.model) ? state.model.layers.find((l) => l.kind === "vector" && l.isBackground && l.colorOptions?.length) : null;
    if (bg) {
      box.append(
        colorRow({
          label: "Background color",
          options: bg.colorOptions,
          get: () => Object.values(state.tints[bg.uuid] ?? {})[0],
          set: (hex) => {
            state.tints[bg.uuid] = { "*": hex };
            syncLineWarns();
            // Thumbnails sit on the paper colour.
            for (const r of box.querySelectorAll(".pbcz-crow")) r.sync?.();
          },
          key: "bg",
          signature: state.originalBg,
        }),
      );
    }
    for (const [i, layer] of accentLayers().entries()) {
      box.append(
        colorRow({
          label: accentLabel(layer, i + 1),
          layer,
          options: layer.colorOptions,
          get: () => tintOf(layer),
          set: (hex) => (state.tints[layer.uuid] = { "*": hex }),
          key: layer.uuid,
          signature: layer.defaultTint,
        }),
      );
    }
    return box.childElementCount ? box : null;
  }

  /**
   * A layer on its own, tinted as it is now, cropped to its busiest patch so a
   * scattered motif (snowflakes over the whole sheet) still reads at 40px.
   */
  const thumbSpots = new Map();
  async function paintLayerThumb(layer, canvas) {
    const m = state.model;
    const solo = await soloRender(layer, 900 / Math.max(m.width, m.height));
    let spot = thumbSpots.get(layer.uuid);
    if (!spot) {
      spot = busiestCell(solo, 14);
      thumbSpots.set(layer.uuid, spot);
    }
    const x = canvas.getContext("2d");
    // On the paper colour, unless the motif would vanish into it.
    const paper = bgHexNow() || m.bgColor || "#FFFFFF";
    const ink = inkOf(solo);
    const faint = ink !== "transparent" && Math.abs(lum(ink) - lum(paper)) < 0.12;
    x.fillStyle = faint ? (lum(ink) > 0.5 ? "#3A2A1E" : "#FFFFFF") : paper;
    x.fillRect(0, 0, canvas.width, canvas.height);
    x.drawImage(solo, spot.x, spot.y, spot.s, spot.s, 0, 0, canvas.width, canvas.height);
  }

  const soloCache = new Map();
  function soloRender(layer, scale) {
    const key = `${state.model.id}|${layer.uuid}|${tintOf(layer)}|${scale.toFixed(4)}`;
    if (!soloCache.has(key)) {
      if (soloCache.size > 40) soloCache.clear();
      const c = document.createElement("canvas");
      const m = state.model;
      const solo = { ...m, layers: [layer], bgColor: null, base: null, artworkPlate: null };
      soloCache.set(key, render(c, solo, { ...renderState(), highlight: null }, { scale, emptySlots: false }).then(() => c));
    }
    return soloCache.get(key);
  }

  /** Average colour of a render's opaque pixels. */
  function inkOf(c) {
    const d = c.getContext("2d", { willReadFrequently: true }).getImageData(0, 0, c.width, c.height).data;
    let r = 0;
    let g = 0;
    let b = 0;
    let n = 0;
    for (let i = 0; i < d.length; i += 16) {
      if (d[i + 3] < 200) continue;
      r += d[i];
      g += d[i + 1];
      b += d[i + 2];
      n += 1;
    }
    if (!n) return "transparent";
    const h = (v) => Math.round(v / n).toString(16).padStart(2, "0");
    return `#${h(r)}${h(g)}${h(b)}`;
  }

  function busiestCell(c, n) {
    const x = c.getContext("2d", { willReadFrequently: true });
    const d = x.getImageData(0, 0, c.width, c.height).data;
    const cell = Math.floor(Math.min(c.width, c.height) / n);
    let best = { x: 0, y: 0, s: cell, hits: -1 };
    // Overlapping windows (half-cell steps) so a motif isn't cut in two.
    for (let cy = 0; cy + cell <= c.height; cy += cell / 2) {
      for (let cx = 0; cx + cell <= c.width; cx += cell / 2) {
        let hits = 0;
        for (let y = cy; y < cy + cell; y += 3) for (let xx = cx; xx < cx + cell; xx += 3) if (d[(Math.floor(y) * c.width + Math.floor(xx)) * 4 + 3] > 40) hits += 1;
        if (hits > best.hits) best = { x: cx, y: cy, s: cell, hits };
      }
    }
    return best;
  }

  /** Hovering a colour row dims everything else on the preview for a moment. */
  function spotlight(layer) {
    state.dim = layer.uuid;
    schedule();
    clearTimeout(spotlight.t);
    spotlight.t = setTimeout(() => {
      state.dim = null;
      schedule();
    }, 600);
  }
  let lineSyncs = [];
  let lineWarns = [];

  function textInputs(counter) {
    const box = el("div", { className: "pbcz-fields" });
    const single = state.model.fields.length === 1;
    const groups = single ? [{ key: "single", fields: state.model.fields }] : fieldGroups();
    lineSyncs = [];
    lineWarns = [];
    groups.forEach((group, gi) => {
      const f = group.fields[0];
      const id = `pbcz-${cfg.sectionId}-${f.key.replace(/\W+/g, "-")}`;
      const maxLen = f.maxLength || (/name/i.test(f.label) ? 14 : 30);
      // A box only when the layer takes line breaks and there's room for them.
      const area = f.multiline && maxLen > 40;
      const input = el(area ? "textarea" : "input", {
        id,
        ...(area ? { rows: 2 } : { type: "text" }),
        className: "pbcz-input",
        value: state.values[f.key] ?? "",
        placeholder:
          (cfg.textDefaults?.[state.model.id]?.[f.key] || "").replace(/\s*\n\s*/g, " ") ||
          (/^name$/i.test(plainLabel(f.label)) ? "Type their name" : GENERIC_LABEL.test(plainLabel(f.label)) ? "Type your words" : `Type ${plainLabel(f.label).toLowerCase()}`),
        autocomplete: "off",
        spellcheck: false,
        maxLength: maxLen,
      });
      if (area) {
        input.classList.add("pbcz-input--area");
        const grow = () => {
          input.style.height = "auto";
          input.style.height = `${input.scrollHeight}px`;
        };
        input.addEventListener("input", grow);
        requestAnimationFrame(grow);
      }
      const count = single ? counter : el("span", { className: "pbcz-count" });
      count.textContent = `${input.value.length} / ${input.maxLength}`;
      input.setAttribute("aria-label", clean(f.label));
      // The box starts with the template's own words: the first tap selects them so typing replaces.
      input.addEventListener("focus", () => input.select(), { once: true });
      input.addEventListener("input", () => {
        count.textContent = `${input.value.length} / ${input.maxLength}`;
        for (const x of group.fields) {
          state.values[x.key] = input.value;
          refreshLabels(x);
        }
        schedule();
        for (const x of group.fields) {
          for (const s2 of steps.querySelectorAll(`[data-field="${CSS.escape(x.key)}"] .pbcz-font__sample`)) s2.textContent = sampleFor(x);
        }
      });
      // A single line is labelled and counted by its step head; it still gets the same Aa + colour buttons.
      // Product Builder designs show the label their maker gave each text ("Message"), even a generic-sounding one.
      const roleText = !state.model.builder && GENERIC_LABEL.test(plainLabel(f.label)) ? `Line ${gi + 1}` : plainLabel(f.label);
      const head = single ? [] : [el("label", { className: "pbcz-label", htmlFor: id, textContent: roleText })];
      const tail = single ? [] : [count];
      if (group.fields.length > 1) {
        const split = el("button", { type: "button", className: "pbcz-link", textContent: "Edit separately" });
        split.title = `This line appears ${group.fields.length} times on the paper`;
        split.addEventListener("click", () => {
          state.unlinked.add(group.key);
          buildControls();
        });
        tail.unshift(split);
      }
      const panel = el("div", { className: "pbcz-drawer pbcz-line__panel", hidden: true });
      const warn = el("p", { className: "pbcz-warn pbcz-line__warn", hidden: true, role: "status" });
      lineWarns.push({ fields: group.fields, el: warn });
      const line = el("div", { className: "pbcz-line" });
      if (!single) line.append(el("div", { className: "pbcz-labelrow" }, [...head, el("span", { className: "pbcz-labelrow__tail" }, tail)]));
      line.append(el("div", { className: "pbcz-line__row" }, [input, ...lineStyle(group, panel, line)]), warn, panel);
      box.append(line);
    });
    // Long templates: the first lines up front, the rest one tap away.
    const SHOWN = 5;
    const lines = [...box.querySelectorAll(":scope > .pbcz-line")];
    if (!single && lines.length > SHOWN + 1) {
      const rest = el("div", { className: "pbcz-morelines", hidden: true });
      rest.append(...lines.slice(SHOWN));
      const more = el("button", { type: "button", className: "pbcz-pill pbcz-pill--out pbcz-pill--sm" }, [
        el("span", { textContent: `Show ${lines.length - SHOWN} more lines` }),
      ]);
      more.setAttribute("aria-expanded", "false");
      more.addEventListener("click", () => {
        rest.hidden = !rest.hidden;
        more.setAttribute("aria-expanded", String(!rest.hidden));
        more.firstChild.textContent = rest.hidden ? `Show ${lines.length - SHOWN} more lines` : "Show fewer lines";
      });
      box.append(rest, more);
    }
    requestAnimationFrame(syncLineWarns);
    return box;
  }


  /** The logos on the paper, each removable, and a tile to add another (they alternate across the paper). */
  function logoList(field, take) {
    const logos = state.logos ?? [];
    // "Custom Logo" talks about logos; "Custom Gift Wrap" (any artwork) about images.
    const noun = /logo/i.test(cfg.uploadKey || "") ? "logo" : "image";
    const wrap = el("div", { className: "pbcz-logos" });
    const row = el("div", { className: "pbcz-logos__row" });
    logos.forEach((t, i) => {
      const thumb = el("div", { className: "pbcz-logos__item" });
      const img = el("img", { alt: `${noun} ${i + 1}`, src: t.upload?.image?.src || t.image?.src || "" });
      thumb.append(img);
      if (logos.length > 1) {
        const x = el("button", { type: "button", className: "pbcz-logos__x", textContent: "×" });
        x.setAttribute("aria-label", `Remove ${noun} ${i + 1}`);
        x.addEventListener("click", async () => {
          state.logos.splice(i, 1);
          const first = state.logos[0];
          if (i === 0) {
            // The next logo becomes the one the editor works on, with the same settings.
            for (const k of ["zoom", "panX", "panY", "rot", "quarter", "tiltDeg", "fit", "sheetFit", "cut", "text"]) first[k] ??= t[k];
            first.zoom ??= 1; first.panX ??= 0; first.panY ??= 0; first.rot ??= 0; first.fit ??= "contain"; first.sheetFit ??= "cover";
            state.upload = first.upload;
            for (const f of state.model.photos ?? []) state.photos[f.key] = first;
          }
          await bake(first, field.key);
          buildControls();
          schedule();
        });
        thumb.append(x);
      }
      row.append(thumb);
    });
    if (logos.length < MAX_LOGOS) {
      const input = el("input", { type: "file", accept: "image/png,image/jpeg,image/webp", className: "pbcz-upload__file" });
      input.multiple = true;
      input.addEventListener("change", async () => {
        for (const f of [...(input.files ?? [])]) await take(f, true);
      });
      row.append(el("label", { className: "pbcz-logos__add" }, [input, el("span", { textContent: `+ Add another ${noun}` })]));
    }
    wrap.append(row);
    if (logos.length > 1) wrap.append(el("p", { className: "pbcz-hint", textContent: `${logos.length} ${noun}s, spread evenly across the paper. Size, rotation and background removal apply to all of them.` }));
    return wrap;
  }

  function uploadControl() {
    const field = state.model.photos?.[0];
    const sheet = isSheet();
    const box = el("div", { className: "pbcz-upload" });
    const input = el("input", { type: "file", accept: "image/png,image/jpeg,image/webp", className: "pbcz-upload__file" });
    const drop = el("label", { className: "pbcz-drop" }, [
      input,
      el("strong", { textContent: state.upload ? "Replace your artwork" : sheet ? "Upload your pattern tile" : "Upload your artwork" }),
      el("span", { textContent: sheet ? "PNG or JPG · one square repeat, ideally 6000 × 6000 px" : "PNG or JPG · a logo, drawing or photo" }),
    ]);
    const report = el("div", { className: "pbcz-quality", "aria-live": "polite" });
    box.append(drop, report);
    // Until they upload, the preview has nothing of theirs to show, so it says what to do instead of sitting
    // blank. The prompt on the preview opens the same file picker as the step.
    let prompt = stageBox.querySelector(".pbcz__awaiting");
    if (!prompt) {
      prompt = el("button", { type: "button", className: "pbcz__awaiting" }, [
        el("strong"),
        el("span", { textContent: "Your wrap shows here as soon as you add it" }),
      ]);
      prompt.addEventListener("click", () => steps.querySelector(".pbcz-upload__file")?.click());
      stageBox.append(prompt);
    }
    prompt.firstChild.textContent = sheet ? "Upload your pattern" : "Upload your logo or image";
    stageBox.classList.toggle("is-awaiting", !state.upload);

    const showQuality = () => {
      const u = state.upload;
      if (!u) return report.replaceChildren();
      if (sheet) return sheetQuality(report);
      const tile = Object.values(state.photos)[0];
      // dpi at the size the tile prints now (set by the last bake).
      const [cls, title, note] = qualityLevel(tile?.printDpi ?? 300);
      report.className = `pbcz-quality ${cls}`;
      report.replaceChildren(
        el("strong", { textContent: title }),
        el("span", { textContent: ` · ${u.width}×${u.height}px · ${u.file.name}` }),
        el("p", { textContent: note }),
      );
    };
    showQuality();
    state.refreshQuality = showQuality;

    const take = async (file, adding = false) => {
      if (!file || !/^image\//.test(file.type)) return;
      const url = URL.createObjectURL(file);
      const image = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = url;
      });
      if (!image) {
        report.className = "pbcz-quality is-low";
        report.textContent = "We couldn't read that file. Try a PNG or JPG.";
        return;
      }
      const upload = { file, image, width: image.naturalWidth, height: image.naturalHeight, url: null };
      const key0 = state.model.photos?.[0]?.key;
      if (adding && state.logos?.length && state.logos.length < MAX_LOGOS) {
        // Another logo: it joins the first one on the paper and follows its settings.
        if (!sheet) upload.staging = stageUpload(upload).catch(() => null);
        state.logos.push({ image, upload });
        if (key0) await bake(state.logos[0], key0);
        buildControls();
        schedule();
        return;
      }
      state.upload = upload;
      // One shared tile fills every slot; the editor below bakes it.
      const tile = { image, zoom: 1, panX: 0, panY: 0, rot: 0, fit: "contain", sheetFit: "cover", upload };
      // Replacing the first logo keeps any others that were added.
      state.logos = [tile, ...(state.logos ?? []).slice(1)];
      for (const f of state.model.photos ?? []) state.photos[f.key] = tile;
      if (key0) await bake(tile, key0);
      // Host the file now, while they finish choosing, so add to cart is quick.
      // (Full-sheet orders host their original with the print file instead.)
      if (!sheet) {
        upload.staging = stageUpload(upload).catch((err) => {
          upload.error = err;
          return null;
        });
      }
      buildControls();
      schedule();
    };

    input.addEventListener("change", () => take(input.files?.[0]));
    drop.addEventListener("dragover", (e) => {
      e.preventDefault();
      drop.classList.add("is-over");
    });
    drop.addEventListener("dragleave", () => drop.classList.remove("is-over"));
    drop.addEventListener("drop", (e) => {
      e.preventDefault();
      drop.classList.remove("is-over");
      take(e.dataTransfer?.files?.[0]);
    });

    if (field && state.upload) {
      const clear = el("button", { type: "button", className: "pbcz-pill pbcz-pill--out pbcz-pill--sm pbcz-removelogo" }, [
        el("span", { textContent: sheet ? "Remove artwork" : "Remove logo" }),
      ]);
      clear.addEventListener("click", () => {
        state.upload = null;
        state.logos = [];
        for (const f of state.model.photos ?? []) delete state.photos[f.key];
        buildControls();
        schedule();
      });
      box.append(clear);
      if (!sheet) box.append(logoList(field, take));
      box.append(sheet ? sheetEditor(field) : uploadEditor(field));
    }
    return box;
  }

  /* ----- full-sheet artwork: the shopper's own 20 in repeat is the print ----- */

  const SHEET_PX = 6000; // one 20 in repeat at 300 dpi
  const PRINT_PPI = 300; // template units are print pixels at 300 per inch

  /** How a picture will print, from its effective dpi at print size. */
  function qualityLevel(dpi) {
    const d = Math.round(dpi);
    if (d >= 200) return ["is-great", "Great quality", `Prints about ${d} dpi. Looks sharp.`];
    if (d >= 150) return ["is-good", "Good quality", `Prints about ${d} dpi. Sharp at arm's length.`];
    if (d >= 100) return ["is-ok", "OK quality", `Prints about ${d} dpi. May look soft up close.`];
    return ["is-low", "Low resolution", `Prints about ${d} dpi, so it may look blurry. Try the original file or a larger export.`];
  }
  const SHEET_IN = 20;

  /** A model whose one upload slot covers the whole sheet (Customily's "Upload Your Own Artwork"). */
  function isSheet(model = state.model) {
    const slots = (model?.layers ?? []).filter((l) => l.kind === "image" && l.uploadable);
    return cfg.mode === "upload" && slots.length === 1 && slots[0].width >= model.width * 0.99 && slots[0].height >= model.height * 0.99;
  }

  /** Source rectangle and placement of the upload on a size x size sheet. */
  function sheetPlacement(tile, size) {
    const src = tile.source ?? tile.image;
    const iw = src.naturalWidth || src.width;
    const ih = src.naturalHeight || src.height;
    if ((tile.sheetFit || "cover") === "cover") {
      const side = Math.min(iw, ih);
      return { src, sx: (iw - side) / 2, sy: (ih - side) / 2, sw: side, sh: side, dx: 0, dy: 0, dw: size, dh: size, used: side };
    }
    const k = size / Math.max(iw, ih);
    const dw = iw * k;
    const dh = ih * k;
    return { src, sx: 0, sy: 0, sw: iw, sh: ih, dx: (size - dw) / 2, dy: (size - dh) / 2, dw, dh, used: Math.max(iw, ih) };
  }

  function drawSheet(x, tile, size, offsetY = 0) {
    const r = sheetPlacement(tile, size);
    x.fillStyle = "#FFFFFF";
    x.fillRect(0, 0, size, size);
    x.drawImage(r.src, r.sx, r.sy, r.sw, r.sh, r.dx, r.dy - offsetY, r.dw, r.dh);
  }

  const LOW_DPI = 100; // below this a print looks blurry up close (qualityLevel's "Low resolution")
  /** Every picture of theirs that would print below LOW_DPI at its size on the paper now: [{ name, dpi }]. */
  function lowResPictures() {
    const out = [];
    if (isSheet()) {
      const tile = Object.values(state.photos)[0];
      if (tile && state.upload) {
        const dpi = sheetPlacement(tile, SHEET_PX).used / SHEET_IN;
        if (dpi < LOW_DPI) out.push({ name: "Your pattern", dpi });
      }
      return out;
    }
    const seen = new Set();
    const fields = state.model?.photos ?? [];
    const many = fields.filter((f) => state.photos[f.key] && !state.photos[f.key].derived).length > 1;
    for (const [i, f] of fields.entries()) {
      const p = state.photos[f.key];
      if (!p || p.derived || seen.has(p) || p.printDpi == null) continue;
      seen.add(p);
      if (p.printDpi < LOW_DPI) out.push({ name: state.upload ? "Your image" : many ? `Photo ${i + 1}` : "Your photo", dpi: p.printDpi });
      // A logo is never stretched past its own pixels (see sizeMax), so a small file prints small instead of blurry.
      else if (state.upload && p.sizeMax != null && p.sizeMax < 0.6) out.push({ name: "Your image", small: true, px: `${state.upload.width} × ${state.upload.height} px` });
    }
    // Extra logos share the first one's size on the paper, so a much smaller file prints softer still.
    const first = state.logos?.[0];
    for (const [i, l] of (state.logos ?? []).slice(1).entries()) {
      const px = Math.max(l.upload?.width || 0, l.upload?.height || 0);
      const px0 = Math.max(first?.upload?.width || 0, first?.upload?.height || 0);
      if (!px || !px0 || first?.printDpi == null) continue;
      const dpi = first.printDpi * Math.min(1, px / px0);
      if (dpi < LOW_DPI) out.push({ name: `Image ${i + 2}`, dpi });
    }
    return out;
  }
  /** The warning above Add to cart. It never blocks the order: some shoppers only have a small file. */
  function paintLowRes() {
    const buy = root.querySelector(".pbcz__buy");
    if (!buy) return;
    let box = root.querySelector("[data-pb-cz-lowres]");
    const low = lowResPictures();
    if (!low.length) { if (box) box.hidden = true; return; }
    if (!box) {
      box = el("div", { className: "pbcz-lowres", role: "status" });
      box.setAttribute("data-pb-cz-lowres", "");
      buy.before(box);
    }
    if (low.every((l) => l.small)) {
      const text = `Your image is a small file (${low[0].px}), so it can only print small without going blurry. Upload a larger file, ideally 1500 px or more on its long side, to print it bigger.`;
      if (box.dataset.text !== text) {
        box.dataset.text = text;
        box.replaceChildren(el("strong", { textContent: "Small image" }), el("p", { textContent: text }));
      }
      box.hidden = false;
      return;
    }
    low.splice(0, low.length, ...low.filter((l) => !l.small));
    const names = low.map((l) => l.name);
    const who = names.length > 2 ? `${names.length} of your pictures` : names.join(" and ");
    const is = low.length > 1 ? "are" : "is";
    const text = `${who} ${is} too low resolution to print sharp (about ${Math.round(Math.min(...low.map((l) => l.dpi)))} dpi; we recommend 150 or more). ${low.length > 1 ? "They" : "It"} may look blurry or pixelated on the paper. Upload the original, larger file, or make ${low.length > 1 ? "them" : "it"} smaller on the paper.`;
    if (box.dataset.text !== text) {
      box.dataset.text = text;
      box.replaceChildren(el("strong", { textContent: "Low resolution image" }), el("p", { textContent: text }));
    }
    box.hidden = false;
  }

  /** Effective print resolution when the repeat prints at 20 in. */
  function sheetQuality(report) {
    const tile = Object.values(state.photos)[0];
    const u = state.upload;
    if (!tile || !u) return report.replaceChildren();
    const px = Math.round(sheetPlacement(tile, SHEET_PX).used);
    // 4000 px across the 20 in repeat = 200 dpi (Great); 3000 Good; 2000 OK.
    const [cls, title, note0] = qualityLevel(px / SHEET_IN);
    const note = note0.replace(" dpi", " dpi at 20 × 20 in") + (px < 4000 ? " 6000 × 6000 px is ideal." : "");
    report.className = `pbcz-quality ${cls}`;
    report.replaceChildren(
      el("strong", { textContent: title }),
      el("span", { textContent: ` · ${u.width}×${u.height}px · ${u.file.name}` }),
      el("p", { textContent: note }),
    );
  }

  /**
   * How well the tile's opposite edges meet: the step across each seam against
   * the image's own row-to-row (column-to-column) texture. >1 means the seam is
   * rougher than anywhere inside the pattern.
   */
  function seamScores(c) {
    const n = 300;
    const t = document.createElement("canvas");
    t.width = n;
    t.height = n;
    const x = t.getContext("2d", { willReadFrequently: true });
    x.drawImage(c, 0, 0, n, n);
    const d = x.getImageData(0, 0, n, n).data;
    const at = (xx, yy, ch) => d[(yy * n + xx) * 4 + ch];
    const rowDiff = (a, b) => {
      let s = 0;
      for (let xx = 0; xx < n; xx += 1) for (let ch = 0; ch < 3; ch += 1) s += Math.abs(at(xx, a, ch) - at(xx, b, ch));
      return s / (n * 3);
    };
    const colDiff = (a, b) => {
      let s = 0;
      for (let yy = 0; yy < n; yy += 1) for (let ch = 0; ch < 3; ch += 1) s += Math.abs(at(a, yy, ch) - at(b, yy, ch));
      return s / (n * 3);
    };
    const inner = (f) => {
      const v = [];
      for (let i = 0; i < n - 1; i += 1) v.push(f(i, i + 1));
      v.sort((a, b) => a - b);
      return v[Math.floor(v.length * 0.75)];
    };
    const rows = inner(rowDiff);
    const cols = inner(colDiff);
    return { tb: rowDiff(n - 1, 0) / (rows + 2), lr: colDiff(n - 1, 0) / (cols + 2) };
  }

  /** Fill or fit, the print resolution, and a tiled seam check. */
  function sheetEditor(field) {
    const tile = state.photos[field.key];
    const wrap = el("div", { className: "pbcz-editor pbcz-sheet" });
    const fitSeg = el("div", { className: "pbcz-seg" });
    const tiled = el("canvas", { className: "pbcz-sheet__tiled", width: 720, height: 720 });
    tiled.setAttribute("role", "img");
    tiled.setAttribute("aria-label", "Your pattern repeated 2 × 2");
    const cropTB = el("canvas", { className: "pbcz-sheet__crop", width: 300, height: 300 });
    const cropLR = el("canvas", { className: "pbcz-sheet__crop", width: 300, height: 300 });
    const seamToggle = el("input", { type: "checkbox", checked: tile.showSeams ?? true });
    const seamNote = el("p", { className: "pbcz-sheet__note", "aria-live": "polite" });
    const paint = () => {
      const b = tile.baked;
      if (!b) return;
      const show = seamToggle.checked;
      const x = tiled.getContext("2d");
      const h = tiled.width / 2;
      for (const [cx, cy] of [[0, 0], [h, 0], [0, h], [h, h]]) x.drawImage(b, cx, cy, h, h);
      if (show) {
        x.save();
        x.strokeStyle = "rgba(255, 32, 96, .9)";
        x.lineWidth = 2;
        x.setLineDash([8, 6]);
        x.beginPath();
        x.moveTo(h, 0);
        x.lineTo(h, tiled.height);
        x.moveTo(0, h);
        x.lineTo(tiled.width, h);
        x.stroke();
        x.restore();
      }
      // Close-ups: the bottom edge meeting the top edge, the right edge meeting the left.
      const W = b.width;
      const H = b.height;
      const k = Math.round(Math.min(W, H) / 16);
      const tb = cropTB.getContext("2d");
      tb.drawImage(b, W / 2 - k, H - k, 2 * k, k, 0, 0, 300, 150);
      tb.drawImage(b, W / 2 - k, 0, 2 * k, k, 0, 150, 300, 150);
      const lr = cropLR.getContext("2d");
      lr.drawImage(b, W - k, H / 2 - k, k, 2 * k, 0, 0, 150, 300);
      lr.drawImage(b, 0, H / 2 - k, k, 2 * k, 150, 0, 150, 300);
      if (show) {
        for (const [c2, v] of [[tb, true], [lr, false]]) {
          c2.save();
          c2.strokeStyle = "rgba(255, 32, 96, .9)";
          c2.lineWidth = 2;
          c2.setLineDash([6, 5]);
          c2.beginPath();
          if (v) {
            c2.moveTo(0, 150);
            c2.lineTo(300, 150);
          } else {
            c2.moveTo(150, 0);
            c2.lineTo(150, 300);
          }
          c2.stroke();
          c2.restore();
        }
      }
      const sc = seamScores(b);
      const off = [];
      if (sc.tb > 2.5) off.push("top and bottom");
      if (sc.lr > 2.5) off.push("left and right");
      tile.seamScore = sc;
      seamNote.className = `pbcz-sheet__note${off.length ? " is-warn" : ""}`;
      seamNote.textContent = off.length
        ? `Your pattern may not line up at the ${off.join(" or the ")}. It will repeat exactly as shown here, so check the close-ups.`
        : "The edges line up, so the pattern should repeat without a visible seam.";
    };
    tile.paintSheet = paint;
    for (const [value, label] of [["cover", "Crop to fill"], ["contain", "Fit whole image"]]) {
      const b = el("button", { type: "button", className: `pbcz-vpill${(tile.sheetFit || "cover") === value ? " is-on" : ""}`, textContent: label });
      b.addEventListener("click", async () => {
        tile.sheetFit = value;
        for (const x2 of fitSeg.children) x2.classList.toggle("is-on", x2 === b);
        await bake(tile, field.key);
        paint();
        const q = wrap.parentElement?.querySelector(".pbcz-quality");
        if (q) sheetQuality(q);
        schedule();
      });
      fitSeg.append(b);
    }
    seamToggle.addEventListener("change", () => {
      tile.showSeams = seamToggle.checked;
      paint();
    });
    wrap.append(
      el("p", { className: "pbcz-sheet__lead", textContent: "Your artwork fills one 20 × 20 in repeat and tiles across the whole roll (19 in wide)." }),
      fitSeg,
      el("span", { className: "pbcz-label", textContent: "Seam check" }),
      el("div", { className: "pbcz-sheet__tiledwrap" }, [tiled]),
      el("label", { className: "pbcz__rights pbcz__rights--inline" }, [seamToggle, el("span", { textContent: "Show seams" })]),
      el("div", { className: "pbcz-sheet__crops" }, [
        el("figure", {}, [cropTB, el("figcaption", { textContent: "Top & bottom seam" })]),
        el("figure", {}, [cropLR, el("figcaption", { textContent: "Left & right seam" })]),
      ]),
      seamNote,
    );
    requestAnimationFrame(paint);
    return wrap;
  }

  /**
   * The 6000 x 6000 px print JPEG (sRGB, 300 dpi). One canvas where the browser
   * allows it; iPhones cap canvases near 16.7M px, so there it's drawn in
   * strips and encoded here.
   */
  async function sheetPrintJpeg(tile, onProgress) {
    if (!window.__pbczForceStrips) {
      const c = document.createElement("canvas");
      c.width = SHEET_PX;
      c.height = SHEET_PX;
      const x = c.getContext("2d");
      if (x) {
        drawSheet(x, tile, SHEET_PX);
        const probe = x.getImageData(SHEET_PX - 1, SHEET_PX - 1, 1, 1).data;
        const blob = probe[3] === 255 ? await new Promise((res) => c.toBlob(res, "image/jpeg", 0.92)) : null;
        c.width = c.height = 1;
        if (blob) return setJpegDpi(blob, 300);
      }
    }
    return stripJpeg(SHEET_PX, SHEET_PX, 0.92, (x, y0, rows) => drawSheet(x, tile, SHEET_PX, y0), onProgress);
  }

  /** Marks a JFIF JPEG as 300 dpi (browsers write 72 or none). */
  async function setJpegDpi(blob, dpi) {
    const head = new Uint8Array(await blob.slice(0, 20).arrayBuffer());
    const jfif = head[2] === 0xff && head[3] === 0xe0 && String.fromCharCode(...head.slice(6, 10)) === "JFIF";
    if (!jfif) return blob;
    head[13] = 1;
    head[14] = dpi >> 8;
    head[15] = dpi & 255;
    head[16] = dpi >> 8;
    head[17] = dpi & 255;
    return new Blob([head, blob.slice(20)], { type: "image/jpeg" });
  }

  /** Public URL in the storage bucket AI prints use (insert-only, anon key). */
  async function hostSheetFile(name, blob, type = "image/jpeg") {
    const host = cfg.sheetHost ?? {};
    if (!host.url || !host.key) throw new Error("the print host isn't set up");
    const base = host.url.replace(/\/+$/, "");
    const res = await fetch(`${base}/storage/v1/object/designs/${name}`, {
      method: "POST",
      headers: { apikey: host.key, Authorization: `Bearer ${host.key}`, "Content-Type": type, "cache-control": "max-age=31536000", "x-upsert": "false" },
      body: blob,
    });
    if (!res.ok) throw new Error(`the print file didn't upload (${res.status})`);
    return `${base}/storage/v1/object/public/designs/${name}`;
  }

  /** Crop / zoom / rotate / position for the uploaded artwork, baked into its tile. */
  function uploadEditor(field) {
    const tile = state.photos[field.key];
    const wrap = el("div", { className: "pbcz-editor" });
    const view = el("canvas", { className: "pbcz-editor__view", width: 240, height: 240 });
    const paintView = () => {
      if (!tile?.baked) return;
      const v = view.getContext("2d");
      const k = Math.min(view.width / tile.baked.width, view.height / tile.baked.height);
      const w = tile.baked.width * k;
      const h = tile.baked.height * k;
      v.clearRect(0, 0, view.width, view.height);
      v.drawImage(tile.baked, (view.width - w) / 2, (view.height - h) / 2, w, h);
      v.strokeStyle = "rgba(167, 93, 18, .5)";
      v.setLineDash([4, 4]);
      v.strokeRect((view.width - w) / 2 + 0.5, (view.height - h) / 2 + 0.5, w - 1, h - 1);
    };
    const redo = async () => {
      await bake(tile, field.key);
      // Size 1 = the largest the artwork fits without meeting its neighbours.
      if (tile.fit === "contain") size.input.max = String(tile.sizeMax ?? 1);
      state.refreshQuality?.();
      paintView();
      schedule();
    };
    const range = (label, min, max, step, key) => {
      const input = el("input", { type: "range", min, max, step, value: String(tile[key] ?? 0), className: "pbcz-range" });
      input.setAttribute("aria-label", label);
      input.addEventListener("input", () => {
        tile[key] = Number(input.value);
        if (key === "tiltDeg") syncRot();
        redo();
      });
      return { input, row: el("label", { className: "pbcz-slider" }, [el("span", { textContent: label }), input]) };
    };
    const cap = zoomCap();
    tile.tiltDeg ??= 0;
    tile.quarter ??= 0;
    const syncRot = () => (tile.rot = tile.quarter * 90 + tile.tiltDeg);
    const size = range("Size", "0.3", "2.5", "0.01", "zoom");
    const tilt = range("Tilt", "-45", "45", "1", "tiltDeg");
    const px = range("Left / right", "-1", "1", "0.01", "panX");
    const py = range("Up / down", "-1", "1", "0.01", "panY");
    tile.tiltDeg ??= 0;
    tile.quarter ??= 0;

    size.input.max = String(tile.fit === "contain" ? (tile.sizeMax ?? 1) : Math.max(1, cap * 1.6));

    const fitSeg = el("div", { className: "pbcz-seg" });
    for (const [value, label] of [["contain", "Fit"], ["cover", "Fill the tile"]]) {
      const b = el("button", { type: "button", className: `pbcz-vpill${tile.fit === value ? " is-on" : ""}`, textContent: label });
      b.addEventListener("click", () => {
        tile.fit = value;
        tile.zoom = 1;
        size.input.max = String(value === "contain" ? (tile.sizeMax ?? 1) : Math.max(1, cap * 1.6));
        size.input.value = String(tile.zoom);
        for (const x of fitSeg.children) x.classList.toggle("is-on", x === b);
        redo();
      });
      fitSeg.append(b);
    }
    const turn = el("button", { type: "button", className: "pbcz-pill pbcz-pill--out pbcz-pill--sm" }, [el("span", { textContent: "Rotate 90°" })]);
    turn.addEventListener("click", () => {
      tile.quarter = (tile.quarter + 1) % 4;
      syncRot();
      redo();
    });
    // Remove background: key out the backdrop colour everywhere (logos: the
    // insides of letters too), or only where it touches the edges.
    tile.source ??= tile.image;
    tile.cut ??= { on: false, tol: 18, mode: /logo/i.test(cfg.uploadKey || "") ? "all" : "edges" };
    const cutToggle = el("input", { type: "checkbox", checked: tile.cut.on });
    const cutTol = el("input", { type: "range", min: "2", max: "60", step: "1", value: String(tile.cut.tol), className: "pbcz-range" });
    cutTol.setAttribute("aria-label", "Background removal strength");
    const applyCut = () => {
      tile.image = tile.cut.on ? cutout(tile.source, tile.cut.tol, tile.cut.mode) : tile.source;
      cutTolRow.hidden = !tile.cut.on;
      cutModeSeg.hidden = !tile.cut.on;
      redo();
    };
    cutToggle.addEventListener("change", () => {
      tile.cut.on = cutToggle.checked;
      applyCut();
    });
    let cutTimer = null;
    cutTol.addEventListener("input", () => {
      tile.cut.tol = Number(cutTol.value);
      clearTimeout(cutTimer);
      cutTimer = setTimeout(applyCut, 120);
    });
    const cutTolRow = el("label", { className: "pbcz-slider", hidden: !tile.cut.on }, [el("span", { textContent: "Strength" }), cutTol]);
    const cutModeSeg = el("div", { className: "pbcz-seg", hidden: !tile.cut.on });
    for (const [v, label] of [["all", "Remove this colour everywhere"], ["edges", "Only around the edges"]]) {
      const b = el("button", { type: "button", className: `pbcz-vpill${tile.cut.mode === v ? " is-on" : ""}`, textContent: label });
      b.addEventListener("click", () => {
        tile.cut.mode = v;
        for (const x2 of cutModeSeg.children) x2.classList.toggle("is-on", x2 === b);
        applyCut();
      });
      cutModeSeg.append(b);
    }

    // Add text next to the logo.
    tile.text ??= { value: "", font: null, color: "#1E1B18", pos: "below" };
    const textInput = el("input", { type: "text", className: "pbcz-input pbcz-input--sm", placeholder: "Add text (optional)", value: tile.text.value, maxLength: 40 });
    textInput.setAttribute("aria-label", "Text on your logo tile");
    let textTimer = null;
    textInput.addEventListener("input", () => {
      tile.text.value = textInput.value;
      textTools.hidden = !textInput.value.trim();
      clearTimeout(textTimer);
      textTimer = setTimeout(redo, 80);
    });
    const posSeg = el("div", { className: "pbcz-seg" });
    for (const [v, label] of [["below", "Below"], ["above", "Above"], ["right", "Right"]]) {
      const b = el("button", { type: "button", className: `pbcz-vpill${tile.text.pos === v ? " is-on" : ""}`, textContent: label });
      b.addEventListener("click", () => {
        tile.text.pos = v;
        for (const x2 of posSeg.children) x2.classList.toggle("is-on", x2 === b);
        redo();
      });
      posSeg.append(b);
    }
    const textField = { key: "__logoText", label: "Logo text", fontOptions: [], defaultFont: tile.text.font, colorOptions: [] };
    state.fonts.__logoText ??= tile.text.font;
    const onFont = (o) => {
      tile.text.font = o.file;
      syncStyle();
      redo();
    };
    const fontsBox = el("div", { className: "pbcz-fontgroup" });
    fontsBox.dataset.field = "__logoText";
    // Same row as every other line of words: the box, then Aa (font) and the colour dot, each opening its
    // choices right under the row.
    const glyph = el("span", { className: "pbcz-line__glyph", textContent: "Aa" });
    const aa = el("button", { type: "button", className: "pbcz-line__aa" }, [glyph]);
    const dot = el("button", { type: "button", className: "pbcz-line__dot" });
    aa.setAttribute("aria-label", "Font for your text");
    dot.setAttribute("aria-label", "Color for your text");
    const syncStyle = () => {
      dot.style.setProperty("--sw", tile.text.color);
      dot.classList.toggle("is-light", lum(tile.text.color) > 0.8);
      if (tile.text.font) loadFont(tile.text.font).then((fam) => fam && (glyph.style.fontFamily = `"${fam}", serif`));
    };
    const colorPane = swatches([], () => tile.text.color, (o) => {
      tile.text.color = o.hex;
      syncStyle();
      redo();
    }, { key: "__logoText", label: "Text color" });
    const stylePanel = el("div", { className: "pbcz-drawer pbcz-line__panel", hidden: true }, [fontsBox, colorPane]);
    const showPane = (which) => {
      const open = !stylePanel.hidden && stylePanel.dataset.pane === which;
      stylePanel.hidden = open;
      stylePanel.dataset.pane = which;
      // .pbcz-drawer stays see-through until it is marked open (the fade-in every other drawer uses).
      stylePanel.classList.remove("is-open");
      if (!open) requestAnimationFrame(() => stylePanel.classList.add("is-open"));
      fontsBox.hidden = which !== "font";
      colorPane.hidden = which !== "color";
      aa.setAttribute("aria-expanded", String(!open && which === "font"));
      dot.setAttribute("aria-expanded", String(!open && which === "color"));
    };
    aa.addEventListener("click", () => showPane("font"));
    dot.addEventListener("click", () => showPane("color"));
    syncStyle();
    const textLine = el("div", { className: "pbcz-line" }, [el("div", { className: "pbcz-line__row" }, [textInput, aa, dot]), stylePanel]);
    const textTools = el("div", { className: "pbcz-editor__text", hidden: !tile.text.value.trim() }, [
      el("span", { className: "pbcz-label", textContent: "Position" }),
      posSeg,
    ]);
    // Fonts need the catalog for a default; fill the chips once it's in.
    loadFontCatalog().then((all) => {
      if (!tile.text.font) {
        tile.text.font = all.find((f) => /sans|montserrat|poppins/i.test(f.name))?.file ?? all[0]?.file;
        // Text typed before the default font arrived was baked in the fallback face.
        if (tile.text.value.trim()) redo();
      }
      textField.defaultFont = tile.text.font;
      state.fonts.__logoText = tile.text.font;
      syncStyle();
      fontsBox.append(fontChips(textField, onFont), moreFonts(textField, onFont));
    });

    wrap.append(
      el("div", { className: "pbcz-editor__top" }, [view, el("div", { className: "pbcz-editor__tools" }, [fitSeg, turn])]),
      size.row,
      tilt.row,
      px.row,
      py.row,
      el("label", { className: "pbcz__rights pbcz__rights--inline" }, [cutToggle, el("span", { textContent: "Remove background" })]),
      cutModeSeg,
      cutTolRow,
      el("span", { className: "pbcz-label", textContent: "Text" }),
      textLine,
      textTools,
    );
    requestAnimationFrame(paintView);
    return wrap;
  }

  /* ----- baking: photo -> its slot's exact print-size tile ----- */

  /**
   * Customily's slot masks are luminance images (black = keep), which the
   * renderer's alpha masking can't use, so the preview and the print would
   * both show square photos. Each photo is instead baked here into its slot at
   * print size: fitted, zoomed, positioned, rotated and cut to the mask. The
   * hosted PNG is what the print replays, so preview and print stay identical.
   */
  const maskCache = new Map();
  async function alphaMask(rel, w, h) {
    const key = `${rel}|${w}x${h}`;
    if (maskCache.has(key)) return maskCache.get(key);
    const job = (async () => {
      const img = await loadImage(rel);
      if (!img) return null;
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      const x = c.getContext("2d", { willReadFrequently: true });
      x.drawImage(img, 0, 0, w, h);
      const d = x.getImageData(0, 0, w, h);
      const px = d.data;
      // Masks that already carry transparency are used as they are.
      const hasAlpha = px[3] < 250 || px[px.length - 1] < 250;
      if (!hasAlpha) {
        for (let i = 0; i < px.length; i += 4) {
          const lum = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
          px[i + 3] = 255 - lum;
        }
        x.putImageData(d, 0, 0);
      }
      return c;
    })();
    maskCache.set(key, job);
    return job;
  }

  function fitRect(iw, ih, bw, bh, fit, zoom, panX, panY) {
    const base = fit === "contain" ? Math.min(bw / iw, bh / ih) : Math.max(bw / iw, bh / ih);
    const k = base * Math.max(0.2, zoom || 1);
    const w = iw * k;
    const h = ih * k;
    const ex = Math.max(0, w - bw);
    const ey = Math.max(0, h - bh);
    return { x: -w / 2 - (panX || 0) * (ex / 2), y: -h / 2 - (panY || 0) * (ey / 2), w, h };
  }

  /** The first image layer a photo field fills (its clones share the tile). */
  function slotLayer(key) {
    return state.model.layers.find((l) => l.kind === "image" && l.uploadable && l.field === key);
  }

  function bakeSize(layer) {
    const scale = Math.min(1, 2400 / Math.max(layer.width, layer.height));
    return [Math.max(1, Math.round(layer.width * scale)), Math.max(1, Math.round(layer.height * scale))];
  }

  /** Masks are 6000px PNGs; decode them while the shopper is still choosing photos. */
  function warmMasks() {
    for (const f of state.model.photos ?? []) {
      const layer = slotLayer(f.key);
      if (layer?.mask) alphaMask(layer.mask, ...bakeSize(layer));
    }
  }

  async function bake(p, key) {
    const layer = slotLayer(key);
    if (!layer || !p.image) return;
    const [w, h] = bakeSize(layer);
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    const x = c.getContext("2d");
    // Upload / logo products only: photo templates' entries carry .upload too.
    if (cfg.mode === "upload") {
      await bakeUpload(p, layer, c);
      p.baked = c;
      p.rev = (p.rev || 0) + 1;
      queueHosting(p);
      if (state.logos?.[0] === p) await bakeMoreLogos(p, layer);
      return;
    }
    // Logo tiles can carry a line of text: it takes a band of the tile and the
    // artwork fits the rest. Both are baked, so the print matches.
    const words = String(p.text?.value || "").trim();
    let ix = 0;
    let iy = 0;
    let iw2 = w;
    let ih2 = h;
    let band = null;
    if (words) {
      const pos = p.text.pos || "below";
      if (pos === "right") {
        iw2 = Math.round(w * 0.6);
        band = { x: iw2, y: 0, w: w - iw2, h };
      } else {
        ih2 = Math.round(h * 0.8);
        if (pos === "above") iy = h - ih2;
        band = { x: 0, y: pos === "above" ? 0 : ih2, w, h: h - ih2 };
      }
    }
    const rot = ((p.rot || 0) * Math.PI) / 180;
    const quarter = Math.round((p.rot || 0) / 90) % 2 !== 0;
    x.save();
    x.beginPath();
    x.rect(ix, iy, iw2, ih2);
    x.clip();
    x.translate(ix + iw2 / 2, iy + ih2 / 2);
    x.rotate(rot);
    const iw = p.image.naturalWidth || p.image.width;
    const ih = p.image.naturalHeight || p.image.height;
    const r = quarter
      ? fitRect(iw, ih, ih2, iw2, p.fit || layer.fit || "cover", p.zoom, p.panY, -(p.panX || 0))
      : fitRect(iw, ih, iw2, ih2, p.fit || layer.fit || "cover", p.zoom, p.panX, p.panY);
    x.drawImage(p.image, r.x, r.y, r.w, r.h);
    // Source pixels per inch of print (canvas px -> print px -> inches).
    p.printDpi = (iw * PRINT_PPI) / (r.w / (c.width / layer.width));
    x.restore();
    const pos0 = p.text?.pos || "below";
    if (band) {
      const family = (await loadFont(p.text.font)) || "serif";
      let size = pos0 === "right" ? band.h * 0.16 : band.h * 0.62;
      x.font = `${size}px "${family}", serif`;
      const pad = band.w * 0.06;
      const wide = x.measureText(words).width;
      if (wide > band.w - pad * 2) size *= (band.w - pad * 2) / wide;
      x.font = `${size}px "${family}", serif`;
      x.fillStyle = p.text.color || "#1E1B18";
      x.textAlign = "center";
      x.textBaseline = "middle";
      x.fillText(words, band.x + band.w / 2, band.y + band.h / 2);
    }
    x.setTransform(1, 0, 0, 1, 0, 0);
    if (layer.mask) {
      const m = await alphaMask(layer.mask, w, h);
      if (m) {
        x.globalCompositeOperation = "destination-in";
        x.drawImage(m, 0, 0);
      }
    }
    p.baked = c;
    p.rev = (p.rev || 0) + 1;
    queueHosting(p);
  }

  /**
   * The largest scale a bw x bh box (in the slot's own frame, centred) can
   * take before it leaves the slot or runs into a neighbouring copy. Repeat
   * slots can overlap each other (the angled logo grid does), so fitting the
   * slot alone isn't enough.
   */
  function safeScale(layer, bw, bh) {
    const slot = Math.min(layer.width / bw, layer.height / bh);
    let near = Infinity;
    const a = (-(layer.rotation || 0) * Math.PI) / 180;
    const reach = 3 * Math.max(layer.width, layer.height);
    for (const o of state.model.layers) {
      if (o === layer || o.kind !== "image" || !o.uploadable) continue;
      const dx = o.centerX - layer.centerX;
      const dy = o.centerY - layer.centerY;
      if (Math.abs(dx) > reach || Math.abs(dy) > reach || (!dx && !dy)) continue;
      const du = Math.abs(dx * Math.cos(a) - dy * Math.sin(a));
      const dv = Math.abs(dx * Math.sin(a) + dy * Math.cos(a));
      // Two copies clear each other when they're apart on either axis.
      near = Math.min(near, Math.max(du / bw, dv / bh));
    }
    return { slot, near };
  }

  /** The logo with its line of text, in the logo's own orientation. */
  async function composeLogo(p) {
    const img = p.image;
    const iw0 = img.naturalWidth || img.width;
    const ih0 = img.naturalHeight || img.height;
    const k = Math.min(1, 2400 / Math.max(iw0, ih0));
    const iw = Math.max(1, Math.round(iw0 * k));
    const ih = Math.max(1, Math.round(ih0 * k));
    const words = String(p.text?.value || "").trim();
    const c = document.createElement("canvas");
    if (!words) {
      c.width = iw;
      c.height = ih;
      c.getContext("2d").drawImage(img, 0, 0, iw, ih);
      return { canvas: c, iw, ih, px: iw0 / iw };
    }
    const family = (await loadFont(p.text.font)) || "serif";
    const pos = p.text.pos || "below";
    const unit = Math.max(iw, ih);
    const m = document.createElement("canvas").getContext("2d");
    let size = unit * (pos === "right" ? 0.14 : 0.16);
    m.font = `${size}px "${family}", serif`;
    const most = pos === "right" ? unit * 1.6 : Math.max(iw, unit * 0.9) * 1.25;
    let tw = m.measureText(words).width;
    if (tw > most) {
      size *= most / tw;
      tw = most;
    }
    const line = size * 1.2;
    const gap = size * 0.35;
    if (pos === "right") {
      c.width = Math.ceil(iw + gap + tw);
      c.height = Math.ceil(Math.max(ih, line));
    } else {
      c.width = Math.ceil(Math.max(iw, tw));
      c.height = Math.ceil(ih + gap + line);
    }
    const x = c.getContext("2d");
    const ix = pos === "right" ? 0 : (c.width - iw) / 2;
    const iy = pos === "right" ? (c.height - ih) / 2 : pos === "above" ? c.height - ih : 0;
    x.drawImage(img, ix, iy, iw, ih);
    x.font = `${size}px "${family}", serif`;
    x.fillStyle = p.text.color || "#1E1B18";
    x.textBaseline = "middle";
    if (pos === "right") {
      x.textAlign = "left";
      x.fillText(words, iw + gap, c.height / 2);
    } else {
      x.textAlign = "center";
      x.fillText(words, c.width / 2, pos === "above" ? line / 2 : ih + gap + line / 2);
    }
    return { canvas: c, iw, ih, px: iw0 / iw };
  }

  /**
   * Upload / logo tiles. "Fit" treats the logo and its text as one piece:
   * rotated together, contained with a margin inside the room the slot has
   * before it meets its neighbours, never cropped. "Fill" covers that room.
   */
  async function bakeUpload(p, layer, c) {
    const x = c.getContext("2d");
    if (isSheet()) {
      // The slot is the whole repeat: draw it exactly as the print file will be.
      drawSheet(x, p, c.width);
      p.sizeMax = null;
      p.paintSheet?.();
      return;
    }
    const kc = c.width / layer.width;
    const rot = ((p.rot || 0) * Math.PI) / 180;
    if ((p.fit || "contain") === "contain") {
      const comp = await composeLogo(p);
      const cw = comp.canvas.width;
      const ch = comp.canvas.height;
      const cos = Math.abs(Math.cos(rot));
      const sin = Math.abs(Math.sin(rot));
      const bw = cw * cos + ch * sin;
      const bh = cw * sin + ch * cos;
      const { slot, near } = safeScale(layer, bw, bh);
      const sMax = Math.min(slot, near) * 0.94;
      // Past 1 source pixel per print pixel the logo blurs; cap Size there.
      const sharp = comp.px / sMax;
      p.sizeMax = Math.max(0.3, Math.min(1, sharp));
      const zoom = Math.min(p.sizeMax, Math.max(0.3, p.zoom || 1));
      const sc = sMax * zoom;
      p.printDpi = (PRINT_PPI * comp.px) / sc;
      const roomX = ((sMax - sc) * bw) / 2;
      const roomY = ((sMax - sc) * bh) / 2;
      x.translate(c.width / 2 + (p.panX || 0) * roomX * kc, c.height / 2 + (p.panY || 0) * roomY * kc);
      x.rotate(rot);
      x.drawImage(comp.canvas, (-cw * sc * kc) / 2, (-ch * sc * kc) / 2, cw * sc * kc, ch * sc * kc);
      x.setTransform(1, 0, 0, 1, 0, 0);
      return;
    }
    // Fill: the slot's own shape, shrunk until it clears the neighbours.
    const { slot, near } = safeScale(layer, layer.width, layer.height);
    const s = Math.min(slot, near * 0.94);
    const fw = layer.width * s * kc;
    const fh = layer.height * s * kc;
    const fx = (c.width - fw) / 2;
    const fy = (c.height - fh) / 2;
    const words = String(p.text?.value || "").trim();
    const pos = p.text?.pos || "below";
    let area = { x: fx, y: fy, w: fw, h: fh };
    let band = null;
    if (words) {
      if (pos === "right") {
        area = { x: fx, y: fy, w: fw * 0.62, h: fh };
        band = { x: fx + area.w, y: fy, w: fw - area.w, h: fh };
      } else {
        const bh = fh * 0.22;
        area = { x: fx, y: pos === "above" ? fy + bh : fy, w: fw, h: fh - bh };
        band = { x: fx, y: pos === "above" ? fy : fy + area.h, w: fw, h: bh };
      }
    }
    const iw = p.image.naturalWidth || p.image.width;
    const ih = p.image.naturalHeight || p.image.height;
    const quarter = Math.round((p.rot || 0) / 90) % 2 !== 0;
    x.save();
    x.beginPath();
    x.rect(area.x, area.y, area.w, area.h);
    x.clip();
    x.translate(area.x + area.w / 2, area.y + area.h / 2);
    x.rotate(rot);
    const r = quarter
      ? fitRect(iw, ih, area.h, area.w, "cover", p.zoom, p.panY, -(p.panX || 0))
      : fitRect(iw, ih, area.w, area.h, "cover", p.zoom, p.panX, p.panY);
    x.drawImage(p.image, r.x, r.y, r.w, r.h);
    // Source pixels per inch of print (canvas px -> print px -> inches).
    p.printDpi = (iw * PRINT_PPI) / (r.w / (c.width / layer.width));
    x.restore();
    if (band) {
      const family = (await loadFont(p.text.font)) || "serif";
      let size = pos === "right" ? band.h * 0.16 : band.h * 0.6;
      x.font = `${size}px "${family}", serif`;
      const pad = band.w * 0.06;
      const wide = x.measureText(words).width;
      if (wide > band.w - pad * 2) size *= (band.w - pad * 2) / wide;
      x.font = `${size}px "${family}", serif`;
      x.fillStyle = p.text.color || "#1E1B18";
      x.textAlign = "center";
      x.textBaseline = "middle";
      x.fillText(words, band.x + band.w / 2, band.y + band.h / 2);
    }
    p.sizeMax = null;
  }

  /** Hosts a baked tile once edits settle, so add to cart doesn't wait on it. */
  function queueHosting(p) {
    clearTimeout(p.hostTimer);
    p.hostTimer = setTimeout(() => hostBaked(p).catch(() => {}), 1800);
  }

  function hostBaked(p) {
    const rev = p.rev;
    if (p.hosted?.rev === rev) return p.hosted.job;
    const job = stageCanvas(p.baked).then((url) => {
      if (p.hosted?.rev === rev) p.hosted.url = url;
      return url;
    });
    p.hosted = { rev, job, url: null };
    return job;
  }

  /** Head cutout (face templates); the module and its model load on first use. */
  const cutoutModel = () => `${api}/models/selfie_multiclass_256x256.tflite`;
  let cutoutMod = null;
  const cutoutModule = () => (cutoutMod ??= import(window.__pbcz.cutout));
  async function headCut(img) {
    const m = await cutoutModule();
    return m.cutoutHead(img, cutoutModel());
  }
  headCut.warm = () => cutoutModule().then((m) => m.loadCutout(cutoutModel())).catch(() => {});

  /** Decodes a chosen file; returns null when the browser can't read it. */
  async function readUpload(file) {
    if (!file || !/^image\//.test(file.type)) return null;
    const url = URL.createObjectURL(file);
    const image = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = url;
    });
    if (!image) return null;
    return { file, image, width: image.naturalWidth, height: image.naturalHeight, url: null, thumb: url, maxEdge: 3000 };
  }

  const photoLayer = (field) =>
    state.model.layers.find((l) => l.kind === "image" && l.uploadable && (l.field === field.key || l.uuid === field.elements?.[0]?.uuid));

  /**
   * Photo templates: one slot per photo field (their cloned tiles share it),
   * upload / replace / remove, zoom and position, and "Add photos" to fill
   * the empty slots in order. Each file is hosted as soon as it's chosen.
   */
  function photoStep(meta) {
    // Some Customily templates split ONE photo across two fields: the first few frames under its own name and
    // the rest under a clone group called "Image Upload N" (Bros Being Bros: Image (2) ×3 + Image Upload 3 ×33).
    // The clone group is not another photo to ask for: it always wears its partner's photo.
    const allFields = state.model.photos ?? [];
    const twinOf = {};
    let lead = null;
    for (const f of allFields) {
      if (/^Image Upload \d+$/.test(f.key) && (f.elements?.length ?? f.count ?? 0) > 1 && lead) twinOf[f.key] = lead.key;
      else lead = f;
    }
    const fields = allFields.filter((f) => !twinOf[f.key]);
    // ?pbczcutout forces it on any photo template, for testing.
    const cutoutAll = FACE_CUTOUT.has(cfg.handle) || /[?&]pbczcutout\b/.test(location.search);
    // Product Builder designs mark the cut-out per frame ("Head cut-out" style), so one design can mix
    // head frames with ordinary photo frames.
    const cutoutFor = (f) => cutoutAll || !!f?.cutout;
    const cutout = cutoutAll || fields.some((f) => f.cutout);
    const box = el("div", { className: "pbcz-photos" });
    const slots = el("div", { className: "pbcz-slots" });
    const editor = el("div", { className: "pbcz-slot-editor" });
    const note = el("p", { className: "pbcz-hint", "aria-live": "polite" });
    const bulk = el("input", { type: "file", accept: "image/*", multiple: true, className: "pbcz-upload__file" });
    const one = el("input", { type: "file", accept: "image/*", hidden: true });
    const addAll = el("label", { className: "pbcz-drop pbcz-drop--sm" }, [
      bulk,
      el("strong", { textContent: fields.length > 1 ? "Add photos" : "Add a photo" }),
      el("span", { textContent: fields.length > 1 ? `${fields.length} spots · they fill in order` : "JPG or PNG · the original works best" }),
    ]);
    let active = fields[0]?.key;
    let pendingSlot = null;

    const own = (f) => state.photos[f.key] && !state.photos[f.key].derived;
    const filled = () => fields.filter(own).length;

    /**
     * Frames they didn't fill repeat their photos in order (1, 2, 3, 1, 2, 3…),
     * each baked for its own frame, so the print never has an empty frame.
     */
    async function fillCycle() {
      const sources = fields.filter(own).map((f) => state.photos[f.key]);
      for (const f of fields) if (state.photos[f.key]?.derived) delete state.photos[f.key];
      if (!sources.length) { await fillTwins(); return; }
      let i = 0;
      for (const f of fields) {
        if (own(f)) continue;
        const src = sources[i++ % sources.length];
        const copy = { image: src.image, fit: src.fit, zoom: src.zoom, panX: src.panX, panY: src.panY, derived: true };
        state.photos[f.key] = copy;
        await bake(copy, f.key);
      }
      await fillTwins();
    }
    async function fillTwins() {
      for (const [key, of] of Object.entries(twinOf)) {
        const src = state.photos[of];
        if (!src) { delete state.photos[key]; continue; }
        const copy = { image: src.image, fit: src.fit, zoom: src.zoom, panX: src.panX, panY: src.panY, derived: true };
        state.photos[key] = copy;
        await bake(copy, key);
      }
    }
    const flash = (field) => {
      const uuid = field.elements?.[0]?.uuid ?? photoLayer(field)?.uuid;
      state.highlight = uuid ?? null;
      schedule();
      clearTimeout(flash.t);
      flash.t = setTimeout(() => {
        state.highlight = null;
        schedule();
      }, 1400);
    };

    async function take(field, file) {
      const up = await readUpload(file);
      if (!up) {
        note.textContent = "We couldn't read that file. Try a JPG or PNG.";
        return;
      }
      const entry = { image: up.image, zoom: 1, panX: 0, panY: 0, upload: up };
      const cutHere = cutoutFor(field);
      if (cutHere) {
        note.textContent = "Cutting out the face…";
        const cut = await headCut(up.image).catch(() => null);
        entry.source = up.image;
        if (cut?.found) {
          entry.cutImage = cut.canvas;
          entry.image = cut.canvas;
          entry.fit = "contain";
          entry.cut = true;
          note.textContent = "";
        } else {
          entry.cut = false;
          note.textContent = cut
            ? "We couldn't find a face in this photo, so it will print as it is. A clear, front-facing photo works best."
            : "We couldn't cut out the face just now, so the photo will print as it is.";
        }
      }
      state.photos[field.key] = entry;
      await bake(entry, field.key);
      await fillCycle();
      if (!cutHere || entry.cut) {
        const [, , text] = qualityLevel(entry.printDpi ?? 300);
        note.textContent = (entry.printDpi ?? 300) < 150 ? `That photo is small for its spot. ${text}` : "";
      }
    }

    function paint() {
      meta.textContent = `${filled()} / ${fields.length}`;
      const repeating = filled() > 0 && filled() < fields.length;
      cycleNote.hidden = !repeating;
      slots.replaceChildren();
      fields.forEach((f, i) => {
        const p = state.photos[f.key];
        const mine = own(f);
        const b = el("button", {
          type: "button",
          className: `pbcz-slot${mine ? " has-photo" : ""}${p?.derived ? " is-repeat" : ""}${f.key === active ? " is-on" : ""}`,
        }, [el("span", { textContent: mine ? "" : "+" }), el("small", { textContent: String(i + 1) })]);
        if (mine) {
          // × deletes this photo; the frame then repeats one of the others.
          const x = el("span", { className: "pbcz-slot__x", textContent: "×", role: "button", tabIndex: 0 });
          x.setAttribute("aria-label", `Delete photo ${i + 1}`);
          const del = async (e) => {
            e.stopPropagation();
            e.preventDefault();
            delete state.photos[f.key];
            await fillCycle();
            paint();
            schedule();
          };
          x.addEventListener("click", del);
          x.addEventListener("keydown", (e) => (e.key === "Enter" || e.key === " ") && del(e));
          b.append(x);
        }
        if (p?.baked) {
          // The slot shows the photo as it sits in its frame.
          const t = el("canvas", { className: "pbcz-slot__img", width: 128, height: Math.round((128 * p.baked.height) / p.baked.width) });
          t.getContext("2d").drawImage(p.baked, 0, 0, t.width, t.height);
          b.prepend(t);
        }
        b.setAttribute("aria-label", `${clean(f.label)}${mine ? " (added)" : p ? " (repeats another photo)" : " (empty)"}`);
        b.addEventListener("click", () => {
          active = f.key;
          if (!mine) {
            pendingSlot = f;
            one.click();
          }
          paint();
          flash(f);
        });
        slots.append(b);
      });
      paintEditor();
    }

    function paintEditor() {
      editor.replaceChildren();
      const f = fields.find((x) => x.key === active);
      const p = f && own(f) ? state.photos[f.key] : null;
      if (!p) return;
      const slider = (label, min, max, step, key) => {
        const input = el("input", { type: "range", min, max, step, value: String(p[key] ?? (key === "zoom" ? 1 : 0)), className: "pbcz-range" });
        input.setAttribute("aria-label", `${label}, photo ${fields.indexOf(f) + 1}`);
        input.addEventListener("input", async () => {
          p[key] = Number(input.value);
          await bake(p, f.key);
          schedule();
        });
        input.addEventListener("change", async () => {
          await fillCycle();
          paint();
          schedule();
        });
        return el("label", { className: "pbcz-slider" }, [el("span", { textContent: label }), input]);
      };
      const replace = el("button", { type: "button", className: "pbcz-link", textContent: "Replace" });
      replace.addEventListener("click", () => {
        pendingSlot = f;
        one.click();
      });
      const remove = el("button", { type: "button", className: "pbcz-link", textContent: "Remove" });
      remove.addEventListener("click", async () => {
        delete state.photos[f.key];
        await fillCycle();
        paint();
        schedule();
      });
      let cutRow = null;
      if (cutoutFor(f) && p.cutImage) {
        const cb = el("input", { type: "checkbox", checked: !!p.cut });
        cb.addEventListener("change", async () => {
          p.cut = cb.checked;
          p.image = p.cut ? p.cutImage : p.source;
          p.fit = p.cut ? "contain" : undefined;
          await bake(p, f.key);
          await fillCycle();
          paint();
          schedule();
        });
        cutRow = el("label", { className: "pbcz__rights pbcz__rights--inline pbcz-cuttoggle" }, [cb, el("span", { textContent: "Cut out the face" })]);
      }
      const [qcls, qtitle, qtext] = qualityLevel(p.printDpi ?? 300);
      const quality = el("p", { className: `pbcz-quality pbcz-quality--slot ${qcls}` }, [el("strong", { textContent: qtitle }), el("span", { textContent: ` · ${qtext}` })]);
      editor.append(
        el("div", { className: "pbcz-slot-editor__head" }, [
          el("b", { textContent: `Photo ${fields.indexOf(f) + 1}` }),
          el("span", {}, [replace, remove]),
        ]),
        quality,
        ...(cutRow ? [cutRow] : []),
        slider("Zoom", "1", "3", "0.01", "zoom"),
        slider("Left / right", "-1", "1", "0.01", "panX"),
        slider("Up / down", "-1", "1", "0.01", "panY"),
      );
    }

    bulk.addEventListener("change", async () => {
      const files = [...(bulk.files ?? [])];
      bulk.value = "";
      const empty = fields.filter((f) => !own(f));
      const order = [...empty, ...fields.filter(own)];
      for (const [i, file] of files.slice(0, fields.length).entries()) {
        await take(order[i], file);
        paint();
        schedule();
      }
      active = order[Math.min(files.length, fields.length) - 1]?.key ?? active;
      paint();
      schedule();
    });
    one.addEventListener("change", async () => {
      const file = one.files?.[0];
      one.value = "";
      if (!file || !pendingSlot) return;
      await take(pendingSlot, file);
      active = pendingSlot.key;
      pendingSlot = null;
      paint();
      schedule();
    });

    // Slot guides show only while they're adding or arranging photos.
    const guides = (on) => {
      clearTimeout(guides.t);
      if (on) {
        state.showGuides = true;
        schedule();
      } else {
        guides.t = setTimeout(() => {
          state.showGuides = false;
          schedule();
        }, 1500);
      }
    };
    box.addEventListener("pointerenter", () => guides(true));
    if (cutout) {
      const warm = () => headCut.warm();
      box.addEventListener("pointerenter", warm, { once: true });
      box.addEventListener("focusin", warm, { once: true });
    }
    box.addEventListener("pointerleave", () => guides(false));
    box.addEventListener("focusin", () => guides(true));
    box.addEventListener("focusout", () => guides(false));
    state.showGuides = false;

    const cycleNote = el("p", { className: "pbcz-hint", hidden: true, textContent: "We'll repeat your photos to fill every frame." });
    box.append(addAll, one, slots, cycleNote, note, editor);
    if (cfg.mode !== "upload") {
      const rights = el("input", { type: "checkbox", checked: state.rights });
      rights.addEventListener("change", () => (state.rights = rights.checked));
      box.append(el("label", { className: "pbcz__rights pbcz__rights--inline" }, [rights, el("span", { textContent: "I have the rights to use these photos." })]));
    }
    paint();
    return box;
  }

  /**
   * Removes a solid background. "all" keys the backdrop colour out everywhere
   * (the insides of letters too); "edges" only clears what's connected to the
   * border, for logos with that colour in them on purpose. The anti-aliased
   * rim is then un-blended from the backdrop so no light halo is left. Runs on
   * a copy of at most 2400px, more than a logo tile prints at.
   */
  function cutout(img, tol, mode = "all") {
    const scale = Math.min(1, 2400 / Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height));
    const w = Math.max(1, Math.round((img.naturalWidth || img.width) * scale));
    const h = Math.max(1, Math.round((img.naturalHeight || img.height) * scale));
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    const x = c.getContext("2d", { willReadFrequently: true });
    x.drawImage(img, 0, 0, w, h);
    const d = x.getImageData(0, 0, w, h);
    const px = d.data;
    const bgc = borderColor(px, w, h);
    if (!bgc) return c;
    const [br, bg, bb] = bgc;
    const dist = (i) => Math.abs(px[i] - br) + Math.abs(px[i + 1] - bg) + Math.abs(px[i + 2] - bb);
    const lim = tol * 3;
    const clear = new Uint8Array(w * h);
    if (mode === "edges") {
      const stack = [];
      for (let xx = 0; xx < w; xx += 1) stack.push(xx, (h - 1) * w + xx);
      for (let yy = 0; yy < h; yy += 1) stack.push(yy * w, yy * w + w - 1);
      const seen = new Uint8Array(w * h);
      while (stack.length) {
        const k = stack.pop();
        if (seen[k]) continue;
        seen[k] = 1;
        if (dist(k * 4) > lim) continue;
        clear[k] = 1;
        const xx = k % w;
        if (xx > 0) stack.push(k - 1);
        if (xx < w - 1) stack.push(k + 1);
        if (k >= w) stack.push(k - w);
        if (k < w * (h - 1)) stack.push(k + w);
      }
    } else {
      for (let k = 0; k < w * h; k += 1) if (dist(k * 4) <= lim) clear[k] = 1;
    }
    // The rim: pixels within 2px of a cleared one.
    const rim = dilate(dilate(clear, w, h), w, h);
    const bgv = [br, bg, bb];
    for (let k = 0; k < w * h; k += 1) {
      const i = k * 4;
      if (clear[k]) {
        px[i + 3] = 0;
        continue;
      }
      if (!rim[k]) continue;
      // How much of the pixel is not backdrop, per channel; the most wins.
      let a = 0;
      for (let ch = 0; ch < 3; ch += 1) {
        const v = px[i + ch];
        const b = bgv[ch];
        const t = v > b ? (v - b) / (255 - b || 1) : v < b ? (b - v) / (b || 1) : 0;
        if (t > a) a = t;
      }
      a = Math.min(1, a);
      if (a < 0.03) {
        px[i + 3] = 0;
        continue;
      }
      for (let ch = 0; ch < 3; ch += 1) {
        const b = bgv[ch];
        px[i + ch] = Math.max(0, Math.min(255, Math.round(b + (px[i + ch] - b) / a)));
      }
      px[i + 3] = Math.min(px[i + 3], Math.round(a * 255));
    }
    x.putImageData(d, 0, 0);
    return c;
  }

  /** The backdrop colour: the most common colour along the border, or null when the border is already transparent. */
  function borderColor(px, w, h) {
    const buckets = new Map();
    let clearEdge = 0;
    let total = 0;
    const visit = (k) => {
      const i = k * 4;
      total += 1;
      if (px[i + 3] < 128) {
        clearEdge += 1;
        return;
      }
      const q = ((px[i] >> 3) << 10) | ((px[i + 1] >> 3) << 5) | (px[i + 2] >> 3);
      const b = buckets.get(q) ?? [0, 0, 0, 0];
      b[0] += 1;
      b[1] += px[i];
      b[2] += px[i + 1];
      b[3] += px[i + 2];
      buckets.set(q, b);
    };
    for (let xx = 0; xx < w; xx += 1) visit(xx), visit((h - 1) * w + xx);
    for (let yy = 1; yy < h - 1; yy += 1) visit(yy * w), visit(yy * w + w - 1);
    if (clearEdge > total / 2) return null;
    let top = null;
    for (const b of buckets.values()) if (!top || b[0] > top[0]) top = b;
    return top && [top[1] / top[0], top[2] / top[0], top[3] / top[0]];
  }

  /** 3x3 max filter of a 0/1 mask. */
  function dilate(m, w, h) {
    const out = new Uint8Array(w * h);
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        const k = y * w + x;
        if (!m[k]) continue;
        for (let dy = -1; dy <= 1; dy += 1) {
          const yy = y + dy;
          if (yy < 0 || yy >= h) continue;
          for (let dx = -1; dx <= 1; dx += 1) {
            const xx = x + dx;
            if (xx >= 0 && xx < w) out[yy * w + xx] = 1;
          }
        }
      }
    }
    return out;
  }

  /** Upscaling past the slot blurs; cap the size at what the file can fill. */
  function zoomCap() {
    const layer = state.model.layers.find((l) => l.kind === "image" && l.uploadable);
    const u = state.upload;
    if (!layer || !u) return 1;
    const need = Math.max(layer.width, layer.height);
    return Math.max(0.3, Math.min(1, Math.max(u.width, u.height) / need));
  }

  const rgbOf = (hex) => {
    const n = Number.parseInt(String(hex).replace("#", "").slice(0, 6), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  };
  const rgbDist = (a, b) => {
    const [r1, g1, b1] = rgbOf(a);
    const [r2, g2, b2] = rgbOf(b);
    return Math.hypot((r1 - r2) * 0.3, (g1 - g2) * 0.59, (b1 - b2) * 0.11);
  };

  /**
   * Up to 8 readable paper + name pairs from the template's own palettes: the
   * current pair first, then the paper colours that differ most from those
   * already chosen, each with the name colour that reads on it (the template
   * default when it does).
   */
  function colorPairs() {
    const m = state.model;
    const f = m.fields[0];
    const fg = (f?.colorOptions ?? []).filter((c) => c.hex);
    if (!f || !fg.length) return null;
    const bgLayer = bgLocked(m) ? null : m.layers.find((l) => l.kind === "vector" && l.isBackground && l.colorOptions?.length);
    const readableOn = (bgHex) => {
      const bl = lum(bgHex);
      const ok = fg.filter((c) => Math.abs(lum(c.hex) - bl) >= 0.45);
      if (!ok.length) return null;
      return (
        ok.find((c) => c.hex.toLowerCase() === String(state.colors[f.key]).toLowerCase()) ??
        ok.find((c) => c.hex.toLowerCase() === String(f.defaultColor).toLowerCase()) ??
        [...ok].sort((a, b) => Math.abs(lum(b.hex) - bl) - Math.abs(lum(a.hex) - bl))[0]
      );
    };
    if (!bgLayer) {
      // Printed backgrounds: only the name colour changes.
      return { bgLayer: null, field: f, pairs: fg.slice(0, 8).map((c) => ({ bg: null, fg: c })) };
    }
    // Bright papers (plus the template's own) with the name colour that reads on each.
    const fgPool = [...fg, { hex: "#FFFFFF", name: "White" }, { hex: "#1E1B18", name: "Black" }];
    const readableFrom = (bgHex) => {
      const bl = lum(bgHex);
      const ok = fgPool.filter((c) => Math.abs(lum(c.hex) - bl) >= 0.45);
      return (
        ok.find((c) => c.hex.toLowerCase() === String(state.colors[f.key]).toLowerCase()) ??
        ok.find((c) => /^#fff/i.test(c.hex)) ??
        ok.sort((a, b) => Math.abs(lum(b.hex) - bl) - Math.abs(lum(a.hex) - bl))[0]
      );
    };
    const paperOpts = [
      ...(state.originalBg ? [{ hex: state.originalBg, name: nameOf(bgLayer.colorOptions, state.originalBg) }] : []),
      ...BRAND_COLORS.filter((c) => c.name !== "Black"),
    ];
    const cands = paperOpts.map((c) => ({ bg: c, fg: readableFrom(c.hex) })).filter((p) => p.fg);
    if (!cands.length) return null;
    // The template's own starting pair comes first, exactly as it loads.
    const current = String(Object.values(state.tints[bgLayer.uuid] ?? {})[0] || "").toLowerCase();
    const curBg = current ? { hex: current.toUpperCase(), name: nameOf(bgLayer.colorOptions, current) } : null;
    const curFgHex = String(state.colors[f.key] || "");
    const curFg = curFgHex ? { hex: curFgHex, name: nameOf(f.colorOptions, curFgHex) } : null;
    const start = curBg && curFg ? { bg: curBg, fg: curFg } : cands[0];
    if (start && !cands.includes(start)) {
      const i = cands.findIndex((p) => p.bg.hex.toLowerCase() === start.bg.hex.toLowerCase());
      if (i >= 0) cands.splice(i, 1, start);
      else cands.unshift(start);
    }
    const chosen = [start ?? cands[0]];
    while (chosen.length < Math.min(8, cands.length)) {
      let best = null;
      let bestD = -1;
      for (const p of cands) {
        if (chosen.includes(p)) continue;
        const d = Math.min(...chosen.map((q) => rgbDist(p.bg.hex, q.bg.hex)));
        if (d > bestD) {
          bestD = d;
          best = p;
        }
      }
      chosen.push(best);
    }
    return { bgLayer, field: f, pairs: chosen };
  }

  function pairLabel() {
    const m = state.model;
    const f = m.fields[0];
    const bgLayer = m.layers.find((l) => l.kind === "vector" && l.isBackground && l.colorOptions?.length);
    const parts = [];
    if (bgLayer) parts.push(nameOf(bgLayer.colorOptions, Object.values(state.tints[bgLayer.uuid] ?? {})[0]));
    if (f?.colorOptions?.length) parts.push(nameOf(f.colorOptions, state.colors[f.key]));
    return parts.join(" / ");
  }

  /** Warns when a name colour nearly disappears into the paper (luminance gap < 0.2). */
  /** Lines the shopper made harder to read on the paper than the template's own design. */
  function faintFields() {
    const bgLayer = state.model.layers.find((l) => l.kind === "vector" && l.isBackground);
    const bg = bgLayer && Object.values(state.tints[bgLayer.uuid] ?? {})[0];
    if (!bg) return [];
    const origBg = state.originalBg ?? bg;
    const gap = (hex, b) => Math.abs(lum(hex) - lum(b));
    return state.model.fields.filter((f) => {
      const now = state.colors[f.key];
      if (!now) return false;
      const before = gap(state.original?.colors?.[f.key] ?? now, origBg);
      return gap(now, bg) < 0.2 && gap(now, bg) < before - 0.02;
    });
  }

  /** The "hard to read" note on the line it's about. */
  function syncLineWarns() {
    const faint = new Set(faintFields().map((f) => f.key));
    for (const w of lineWarns) {
      const hit = w.fields.some((f) => faint.has(f.key));
      w.el.hidden = !hit;
      w.el.textContent = hit ? "Hard to read on this background. Try a darker or lighter color." : "";
    }
  }

  function readability(warn) {
    syncLineWarns();
    const faint = faintFields();
    warn.hidden = !faint.length;
    const names = faint.map((f) => shownLabel(f));
    warn.textContent = !faint.length
      ? ""
      : `Heads up: ${names.slice(0, 2).join(" and ")}${names.length > 2 ? ` and ${names.length - 2} more line${names.length === 3 ? "" : "s"}` : ""} will be hard to read on this background color.`;
  }

  function colorsStep(meta) {
    const box = el("div", { className: "pbcz-colorstep" });
    const warn = el("p", { className: "pbcz-warn", hidden: true, role: "status" });
    const data = colorPairs();
    const full = colorSteps(() => {
      meta.textContent = pairLabel();
      readability(warn);
      for (const x of box.querySelectorAll(".pbcz-pair")) x.classList.remove("is-on");
    });
    if (!data && !full) return null;
    meta.textContent = pairLabel();
    if (data) {
      const grid = el("div", { className: "pbcz-pairs", role: "radiogroup" });
      grid.setAttribute("aria-label", "Colors");
      const isOn = (p) =>
        (!p.bg || p.bg.hex.toLowerCase() === String(Object.values(state.tints[data.bgLayer.uuid] ?? {})[0]).toLowerCase()) &&
        p.fg.hex.toLowerCase() === String(state.colors[data.field.key]).toLowerCase();
      for (const p of data.pairs) {
        const label = [p.bg?.name, p.fg.name].filter(Boolean).join(" / ") || p.fg.hex;
        const b = el("button", { type: "button", className: `pbcz-pair${isOn(p) ? " is-on" : ""}` }, [
          el("span", {
            className: "pbcz-pair__sw",
            textContent: "Aa",
            style: `background:${p.bg?.hex ?? "var(--pbcz-ground)"};color:${p.fg.hex}`,
          }),
          el("small", { textContent: label }),
        ]);
        b.setAttribute("role", "radio");
        b.setAttribute("aria-label", label);
        b.addEventListener("click", () => {
          if (p.bg) state.tints[data.bgLayer.uuid] = { "*": p.bg.hex };
          state.colors[data.field.key] = p.fg.hex;
          for (const x of grid.children) x.classList.toggle("is-on", x === b);
          meta.textContent = pairLabel();
          readability(warn);
          if (full) {
            const again = colorSteps(() => {
              meta.textContent = pairLabel();
              readability(warn);
              for (const x of grid.children) x.classList.remove("is-on");
            });
            if (again) fullWrap.replaceChildren(again);
          }
          schedule();
        });
        grid.append(b);
      }
      box.append(grid);
    }
    const fullWrap = el("div", { className: "pbcz-allcolors" });
    if (full) fullWrap.append(full);
    box.append(fullWrap, warn);
    readability(warn);
    return box;
  }

  function colorSteps(onChange) {
    const box = el("div", { className: "pbcz-colors" });
    const bg = bgLocked(state.model) ? null : state.model.layers.find((l) => l.kind === "vector" && l.isBackground && l.colorOptions?.length);
    if (bg) {
      const label = el("span", { className: "pbcz-label" }, [
        "Background color ",
        el("em", { textContent: colorText(bg.colorOptions, Object.values(state.tints[bg.uuid] ?? {})[0]) }),
      ]);
      box.append(
        label,
        swatches(bg.colorOptions, () => Object.values(state.tints[bg.uuid] ?? {})[0], (o) => {
          state.tints[bg.uuid] = { "*": o.hex };
          label.lastChild.textContent = colorText(bg.colorOptions, o.hex);
          onChange?.();
          schedule();
        }, { label: "Background color", key: "bg", signature: state.originalBg }),
      );
    }
    for (const f of state.model.fields) {
      if (!f.colorOptions?.length) continue;
      const multi = state.model.fields.length > 1;
      const label = el("span", { className: "pbcz-label" }, [
        multi ? labelNode(f, " color ") : "Name color ",
        el("em", { textContent: colorText(f.colorOptions, state.colors[f.key]) }),
      ]);
      box.append(
        label,
        swatches(f.colorOptions, () => state.colors[f.key], (o) => {
          state.colors[f.key] = o.hex;
          label.lastChild.textContent = colorText(f.colorOptions, o.hex);
          onChange?.();
          schedule();
        }, { label: `${shownLabel(f)} color`, key: f.key, signature: state.original?.colors?.[f.key] }),
      );
    }
    return box.childElementCount ? box : null;
  }

  /** Other recolourable artwork and swappable prints ("Background print"). */
  function artworkStep({ accents = true } = {}) {
    const box = el("div", { className: "pbcz-colors" });
    // Layers under a full-resolution artwork plate never show: their choices would do nothing.
    const plate = artworkPlateCoverage(state.model);
    const covered = (layer) => plate && state.model.layers.indexOf(layer) < plate.upTo;
    if (accents) {
      const rows = colorRows({ background: false });
      if (rows) box.append(rows);
    }
    for (const layer of state.model.layers) {
      if (covered(layer)) continue;
      // Options whose art the studio only has at web size stay hidden (map field 7).
      const off = new Set(cfg.disabledOptions ?? []);
      const opts = (layer.options ?? []).filter((o) => !off.has(`${state.model.id}~${layer.uuid}~${o.id}`));
      if (layer.selectable && state.choices[layer.uuid] && !opts.some((o) => o.file === state.choices[layer.uuid])) delete state.choices[layer.uuid];
      if (layer.selectable && opts.length > 1) {
        const row = el("div", { className: "pbcz-prints", role: "radiogroup" });
        const current = () => state.choices[layer.uuid] ?? layer.file;
        for (const [i, o] of opts.entries()) {
          const on = current() === o.file;
          const b = el("button", {
            type: "button",
            className: `pbcz-print${on ? " is-on" : ""}`,
            title: o.name || `Option ${i + 1}`,
            style: `background-image:url("${fileUrl(o.file)}")`,
          });
          b.setAttribute("aria-label", o.name || `Option ${i + 1}`);
          b.addEventListener("click", async () => {
            state.choices[layer.uuid] = o.file;
            for (const x of row.children) x.classList.toggle("is-on", x === b);
            await loadImage(o.file);
            schedule();
          });
          row.append(b);
        }
        box.append(el("span", { className: "pbcz-label", textContent: clean(layer.name) || "Print" }), row);
      }
    }
    return box.childElementCount ? box : null;
  }

  function layoutStep() {
    if (layouts.length < 2) return null;
    const row = el("div", { className: "pbcz-seg", role: "radiogroup" });
    layouts.forEach((l, i) => {
      const on = i === state.layoutIndex;
      const b = el("button", { type: "button", className: `pbcz-vpill${on ? " is-on" : ""}`, textContent: l.label });
      b.setAttribute("role", "radio");
      b.setAttribute("aria-checked", String(on));
      b.addEventListener("click", () => {
        if (i === state.layoutIndex) return;
        openLayout(i, { carry: true });
      });
      row.append(b);
    });
    return row;
  }

  const bgHexNow = () => {
    const bg = state.model.layers.find((l) => l.kind === "vector" && l.isBackground);
    return bg ? Object.values(state.tints[bg.uuid] ?? {})[0] || state.model.artworkPlateBg || null : null;
  };

  function buildControls() {
    stepNo = 0;
    activeDrawer = null;
    const m = state.model;
    const intro = $(".pbcz__intro");
    if (intro && cfg.mode !== "upload") {
      if (m.photos?.length && cfg.introPhoto) intro.textContent = cfg.introPhoto;
      else if (m.fields.length > 1 && cfg.introPhrase) intro.textContent = cfg.introPhrase;
    }
    const out = [];
    const sheetChoice = cfg.mode === "upload" && layouts.some((l) => /full sheet/i.test(l.label));
    if (sheetChoice) {
      const lay = layoutStep();
      const hint = el("p", {
        className: "pbcz-hint",
        textContent: isSheet() ? "Your own seamless pattern fills the whole paper." : "Your logo or image repeats across the paper.",
      });
      if (lay) out.push(step("Print style", "", el("div", {}, [lay, hint])));
    }
    if (cfg.mode === "upload") {
      out.push(step("Your artwork", "", uploadControl()));
    } else if (m.photos?.length) {
      const photoMeta = el("span", { className: "pbcz-step__meta" });
      out.push(step(m.photos.length > 1 ? "Your photos" : "Your photo", photoMeta, photoStep(photoMeta)));
    }
    if (m.fields.length) {
      // Every design with words works the same way: each line styles itself (Aa + colour dot) right where it is
      // typed, then the paper's own colours follow as one row per element. A single name gets its counter in the
      // step head. Designs with no words fall through to the colour steps below.
      const single = m.fields.length === 1;
      const counter = el("span", { className: "pbcz-step__meta pbcz-count" });
      const title = single && /name/i.test(m.fields[0].label) ? "Their name" : "Your words";
      out.push(step(title, single ? counter : "", textInputs(single ? counter : el("span"))));
      const colors = colorRows();
      if (colors) out.push(step("Colors", "", colors));
      const prints = artworkStep({ accents: false });
      if (prints) out.push(step("Background print", "", prints));
      const lay = layoutStep();
      if (lay) out.push(step("Layout", "", lay));
      out.push(step("Roll length", cfg.rollFacts || "", rollStep()));
      steps.replaceChildren(...out);
      syncPrice();
      return;
    }
    const colorMeta = el("span", { className: "pbcz-step__meta" });
    const colors = colorsStep(colorMeta);
    if (colors) out.push(step("Colors", colorMeta, colors));
    const art = artworkStep();
    if (art) out.push(step(art.querySelector(".pbcz-prints") ? "Background print" : "Accent colors", "", art));
    const lay = sheetChoice ? null : layoutStep();
    if (lay) out.push(step("Layout", "", lay));
    out.push(step("Roll length", cfg.rollFacts || "", rollStep()));
    steps.replaceChildren(...out);
    syncPrice();
  }

  function rollStep() {
    const list = variants();
    const row = el("div", { className: "pbcz-seg pbcz-seg--3", role: "radiogroup" });
    if (!state.size || !list.some((v) => v.title === state.size)) {
      state.size = (list.find((v) => /25/.test(v.title) && v.available) ?? list.find((v) => v.available) ?? list[0])?.title;
    }
    for (const v of list) {
      const on = v.title === state.size;
      const b = el("button", { type: "button", className: `pbcz-vpill pbcz-vpill--2${on ? " is-on" : ""}`, disabled: !v.available }, [
        el("b", { textContent: v.title.replace(/FT$/i, " ft") }),
        el("small", { textContent: v.price }),
      ]);
      b.setAttribute("role", "radio");
      b.setAttribute("aria-checked", String(on));
      b.addEventListener("click", () => {
        state.size = v.title;
        for (const x of row.children) {
          x.classList.toggle("is-on", x === b);
          x.setAttribute("aria-checked", String(x === b));
        }
        syncPrice();
      });
      row.append(b);
    }
    return row;
  }

  function currentVariant() {
    return variants().find((v) => v.title === state.size) ?? variants()[0];
  }

  function syncPrice() {
    const v = currentVariant();
    for (const n of root.querySelectorAll("[data-pb-cz-price]")) n.textContent = v?.price ?? "";
    for (const n of root.querySelectorAll("[data-pb-cz-compare]")) {
      n.textContent = v?.compare ?? "";
      n.hidden = !v?.compare;
    }
  }

  /* ----- saving + cart ----- */

  /**
   * The studio only replays photos it can fetch by URL. It has no upload
   * endpoint, but premade-cart stores the preview image it is given at a
   * public /files URL, so the upload is staged through that. Side effect: one
   * unused Storefront cart and an orphan spec per upload (never rendered —
   * the webhook only renders ids that appear on a paid order).
   */
  async function stageUpload(upload) {
    const max = upload.maxEdge || 4000;
    const scale = Math.min(1, max / Math.max(upload.width, upload.height));
    const c = document.createElement("canvas");
    c.width = Math.round(upload.width * scale);
    c.height = Math.round(upload.height * scale);
    c.getContext("2d").drawImage(upload.image, 0, 0, c.width, c.height);
    const png = upload.file.type === "image/png" || upload.file.type === "image/webp";
    upload.url = await stageCanvas(c, png ? "image/png" : "image/jpeg");
    return upload.url;
  }

  /** Hosts a canvas at a public studio /files URL (see stageUpload's note). */
  async function stageCanvas(c, type = "image/png") {
    let data = c.toDataURL(type, 0.92);
    if (data.length > 18e6) data = c.toDataURL("image/jpeg", 0.9);
    const pid = `upload-${uuid()}`;
    const res = await fetch(`${api}/api/premade-cart`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        handle: cfg.handle,
        templateId: layout().id,
        templateName: "upload staging",
        size: state.size || "10FT",
        attributes: [],
        previewPng: data,
        personalizationId: pid,
        state: {},
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || `upload failed (${res.status})`);
    const origin = new URL(body.productionUrl).origin;
    return `${origin}/files/exports/checkout/${encodeURIComponent(pid)}-preview.png`;
  }

  function visibleProperties() {
    const m = state.model;
    const props = [];
    const bg = m.layers.find((l) => l.kind === "vector" && l.isBackground);
    if (bg) {
      const hex = Object.values(state.tints[bg.uuid] ?? {})[0];
      if (hex) props.push([WRAP_KEY, nameOf(bg.colorOptions, hex)]);
    }
    m.fields.forEach((f, i) => {
      const text = clean(state.values[f.key]);
      if (text) props.push([clean(f.label), text]);
    });
    m.fields.forEach((f, i) => {
      const font = state.fonts[f.key] ? fontName(f) : "";
      const color = nameOf(f.colorOptions, state.colors[f.key]);
      const prefix = i === 0 ? "Font" : `${clean(f.label)}`;
      if (font) props.push([i === 0 ? "Font Style" : `${prefix} Font`, font]);
      if (color) props.push([i === 0 ? "Font Color" : `${prefix} Color`, color]);
    });
    for (const layer of m.layers) {
      if (layer.selectable && state.choices[layer.uuid]) {
        const o = layer.options.find((x) => x.file === state.choices[layer.uuid]);
        if (o?.name) props.push([clean(layer.name), o.name]);
      }
    }
    if (layouts.length > 1) props.push(["Layout", layout().label]);
    return props;
  }

  function specState() {
    const photos = {};
    // The baked tile already carries crop, zoom, position and frame shape.
    for (const [key, p] of Object.entries(state.photos)) {
      const url = p.hosted?.url;
      if (url) photos[key] = { src: url, zoom: 1, panX: 0, panY: 0 };
    }
    const fonts = Object.fromEntries(Object.entries(state.fonts).filter(([k]) => !k.startsWith("__")));
    return {
      values: state.values,
      fonts,
      colors: state.colors,
      choices: state.choices,
      tints: state.tints,
      elementValues: {},
      elementFonts: {},
      elementColors: {},
      photos,
      // Several logos: the studio prints each slot's own image.
      elementPhotos: Object.fromEntries(Object.entries(logoSlots() ?? {}).filter(([, t]) => t.hosted?.url).map(([id, t]) => [id, { src: t.hosted.url, zoom: 1, panX: 0, panY: 0 }])),
    };
  }

  function validate() {
    const m = state.model;
    if (!m) return "Your design is still loading.";
    const photoFields = m.photos ?? [];
    if (cfg.mode === "upload" && !state.upload) return "Upload your artwork first.";
    if (cfg.mode !== "upload" && photoFields.length) {
      // Any number of photos works: empty frames repeat the ones they added.
      if (!photoFields.some((f) => state.photos[f.key] && !state.photos[f.key].derived)) {
        return photoFields.length > 1 ? "Add at least one photo first." : "Add your photo first.";
      }
    }
    if ((cfg.mode === "upload" || photoFields.length) && !state.rights) return "Please confirm you have the rights to use these images.";
    if (m.fields.length && !clean(state.values[m.fields[0].key])) {
      const label = plainLabel(m.fields[0].label);
      return `Add ${/name/i.test(label) ? "their name" : GENERIC_LABEL.test(label) ? "your words" : label.toLowerCase()} first.`;
    }
    if (!currentVariant()) return "Pick a roll length.";
    return null;
  }

  /**
   * Phones press the pinned bar's Add to cart, far from the status line, so a failed check looked like a dead
   * button ("not letting us add picture"). Scroll to what needs doing and say it right there.
   */
  function showProblem(problem) {
    const pick = (sel) => [...root.querySelectorAll(sel)].find((n) => n.offsetParent !== null);
    const target = /rights/.test(problem)
      ? pick(".pbcz-photos .pbcz__rights--inline:not(.pbcz-cuttoggle), [data-pb-cz-rights]")?.closest("label") ??
        pick(".pbcz-photos .pbcz__rights--inline:not(.pbcz-cuttoggle), [data-pb-cz-rights]")
      : /photo|artwork/i.test(problem)
        ? pick(".pbcz-photos .pbcz-drop, .pbcz-upload .pbcz-drop")
        : / first\.$/.test(problem)
          ? pick(".pbcz-fields .pbcz-input")
          : null;
    if (!target) return;
    root.querySelector(".pbcz-problem")?.remove();
    const note = el("p", { className: "pbcz__status is-error pbcz-problem", role: "alert", textContent: problem });
    target.after(note);
    target.classList.add("pbcz-flag");
    setTimeout(() => target.classList.remove("pbcz-flag"), 2400);
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    const clear = () => note.remove();
    target.addEventListener("change", clear, { once: true });
    target.addEventListener("input", clear, { once: true });
  }

  async function previewJpeg() {
    const m = state.model;
    const c = document.createElement("canvas");
    await render(c, m, { ...renderState(), highlight: null }, { scale: Math.min(1, 800 / Math.max(m.width, m.height)), emptySlots: false });
    return c.toDataURL("image/jpeg", 0.88);
  }

  async function addToCart(btn) {
    const problem = validate();
    if (problem) {
      say(problem, true);
      showProblem(problem);
      return;
    }
    const buttons = root.querySelectorAll("[data-pb-cz-add]");
    buttons.forEach((b) => {
      b.disabled = true;
      b.setAttribute("aria-busy", "true");
    });
    say("Saving your design…");
    try {
      if (isSheet()) {
        await addSheetToCart(btn);
        return;
      }
      if (state.model.builder) {
        await addBuiltToCart(btn);
        return;
      }
      const tiles = [...new Set([...Object.values(state.photos), ...(cfg.mode === "upload" ? state.logos ?? [] : [])].filter((p) => p.baked))];
      const uploads = tiles;
      if (tiles.length) say(tiles.length > 1 ? `Saving your ${tiles.length} photos…` : "Saving your design…");
      await Promise.all(
        tiles.map(async (t) => {
          clearTimeout(t.hostTimer);
          const url = await hostBaked(t);
          if (!url) throw new Error("a photo didn't upload");
        }),
      );
      // The untouched original, for the team's reference on upload products.
      let originalUrl = null;
      if (state.upload) originalUrl = state.upload.url || (await state.upload.staging) || (await stageUpload(state.upload));
      const photoUrl = Object.values(state.photos)[0]?.hosted?.url ?? null;
      const variant = currentVariant();
      const pid = `customily-id-${uuid()}`;
      const thumbId = `thumb-id-${Date.now()}`;
      const visible = visibleProperties();
      const res = await fetch(`${api}/api/premade-cart`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          handle: layout().handle,
          templateId: layout().id,
          templateName: state.model.storeTitle || state.model.name,
          side: "eps",
          size: variant.title,
          quantity: state.qty,
          attributes: visible.map(([key, value]) => ({ key, value })),
          previewPng: await previewJpeg(),
          personalizationId: pid,
          thumbId,
          state: specState(),
        }),
      });
      const saved = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(saved.error || `save failed (${res.status})`);

      // Same six values premade-cart writes onto its own Storefront line.
      const origin = new URL(saved.productionUrl).origin;
      const preview = `${origin}/files/exports/checkout/${encodeURIComponent(saved.personalizationId)}-preview.png`;
      const properties = {};
      const arrives = arrivesBy();
      if (arrives) properties["Arrives by"] = arrives;
      for (const [k, v] of appProperties()) properties[k] = v;
      for (const [k, v] of visible) properties[k] = v;
      if (photoUrl && cfg.uploadKey) properties[cfg.uploadKey] = photoUrl;
      if (originalUrl && cfg.uploadKey) properties["_Original upload"] = originalUrl;
      // More logos: one tile + one untouched original each, numbered from 2, and a visible count for the team.
      if (cfg.mode === "upload" && cfg.uploadKey && (state.logos?.length ?? 0) > 1) {
        properties["Logos"] = String(state.logos.length);
        for (const [i, t] of state.logos.entries()) {
          if (i === 0) continue;
          if (t.hosted?.url) properties[`${cfg.uploadKey} ${i + 1}`] = t.hosted.url;
          const orig = t.upload && (t.upload.url || (await t.upload.staging) || (await stageUpload(t.upload).catch(() => null)));
          if (orig) properties[`_Original upload ${i + 1}`] = orig;
        }
      }
      const logoText = Object.values(state.photos)[0]?.text;
      if (cfg.mode === "upload" && logoText?.value?.trim()) {
        properties["Logo Text"] = logoText.value.trim();
        properties["Logo Text Font"] = state.fontNames?.[logoText.font] || nameFromFile(logoText.font);
        properties["Logo Text Color"] = nameOf([], logoText.color);
        properties["Logo Text Position"] = logoText.pos;
      }
      // Photo templates: one hidden key per photo field, named like Customily's ("_Image 1").
      if (cfg.mode !== "upload") {
        for (const f of state.model.photos ?? []) {
          const url = state.photos[f.key]?.hosted?.url;
          if (url) properties[`_${clean(f.label)}`] = url;
        }
      }
      if (uploads.length) properties["_Artwork rights confirmed"] = "Yes";
      Object.assign(properties, {
        "_customily-thumb-id": `_${thumbId}`,
        "_customily-preview": preview,
        "_customily-thumb": preview,
        "_customily-customization-url": editUrl(saved.personalizationId),
        "_customily-production-url": saved.productionUrl,
        "_customily-personalization-id": `_${saved.personalizationId}`,
      });

      if (state.saveOnly) return await finishSave(saved.personalizationId, variant, properties, originalUrl);
      say("Adding to your cart…");
      // Impact's drawer re-renders from sections bundled into the add response.
      const bundle = { sections: [] };
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: bundle }),
      );
      window.PBPreviewTag?.(properties); // checkout picture: see assets/pb.js
      const add = await fetch(`${window.Shopify?.routes?.root ?? "/"}cart/add.js`, {
        method: "POST",
        headers: { "content-type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: state.qty, properties }],
          ...(bundle.sections.length ? { sections: bundle.sections.join(","), sections_url: location.pathname } : {}),
        }),
      });
      const added = await add.json().catch(() => ({}));
      if (!add.ok) {
        throw new Error(
          add.status === 429
            ? "the store is busy right now, so wait a few seconds"
            : added.description || added.message || "we couldn't add it to your cart",
        );
      }
      saveDesign(saved.personalizationId, originalUrl);
      remember(saved.personalizationId, variant, properties);
      askWho(saved.personalizationId);
      const replaced = await replaceEdited();
      await announceCart(added, btn);
      say(replaced ? "Added to your cart, replacing the earlier version." : "Added to your cart.");
    } catch (err) {
      console.error("[pb-customizer]", err);
      say(`Sorry, ${err.message}. Please try again.`, true);
    } finally {
      buttons.forEach((b) => {
        b.disabled = false;
        b.removeAttribute("aria-busy");
      });
    }
  }

  /**
   * The print JPEG for a Product Builder design (model.builder): the same render the preview shows, at the
   * model's full 6000 px, 300 dpi. One canvas where the browser allows it; otherwise four quarters (each under
   * the iPhone canvas limit) fed to the strip encoder.
   */
  async function builtPrintJpeg(onProgress) {
    const m = state.model;
    const st = { ...renderState(), highlight: null };
    if (!window.__pbczForceStrips) {
      const c = document.createElement("canvas");
      await render(c, m, st, { scale: 1, emptySlots: false });
      const probe = c.width === m.width ? c.getContext("2d").getImageData(m.width - 1, m.height - 1, 1, 1).data : [0, 0, 0, 0];
      const blob = probe[3] === 255 ? await new Promise((res) => c.toBlob(res, "image/jpeg", 0.92)) : null;
      c.width = c.height = 1;
      if (blob) return setJpegDpi(blob, 300);
    }
    const hw = m.width / 2;
    const hh = m.height / 2;
    const quads = [];
    for (const qy of [0, 1]) {
      for (const qx of [0, 1]) {
        const c = document.createElement("canvas");
        await render(c, m, st, { scale: 1, emptySlots: false, window: { x: qx * hw, y: qy * hh, w: hw, h: hh } });
        quads.push({ c, x: qx * hw, y: qy * hh });
      }
    }
    const out = await stripJpeg(m.width, m.height, 0.92, (x, y0) => {
      for (const q of quads) x.drawImage(q.c, q.x, q.y - y0);
    }, onProgress);
    for (const q of quads) q.c.width = q.c.height = 1;
    return out;
  }

  /**
   * Product Builder designs: no studio render. The print file is built here from the same model and choices
   * the preview shows, hosted with its thumbnail, and the six Customily keys point at them the way full-sheet
   * and AI prints do.
   */
  async function addBuiltToCart(btn) {
    const variant = currentVariant();
    const id = uuid();
    say("Preparing your print file…");
    await preload(state.model);
    const print = await builtPrintJpeg((f) => say(`Preparing your print file… ${Math.round(f * 100)}%`));
    // A print that lost a piece of artwork must never ship.
    const missing = missingAssets();
    if (missing.length) throw new Error("part of the design didn't load, so refresh the page");
    const thumbC = document.createElement("canvas");
    await render(thumbC, state.model, { ...renderState(), highlight: null }, { scale: 800 / state.model.width, emptySlots: false });
    const thumb = await new Promise((res) => thumbC.toBlob(res, "image/jpeg", 0.85));
    say("Uploading your print file…");
    const [printUrl, thumbUrl] = await Promise.all([hostSheetFile(`built-prod-${id}.jpg`, print), hostSheetFile(`built-thumb-${id}.jpg`, thumb)]);
    const properties = {};
    const arrives = arrivesBy();
    if (arrives) properties["Arrives by"] = arrives;
    for (const [k, v] of appProperties()) properties[k] = v;
    for (const [k, v] of visibleProperties()) properties[k] = v;
    if ((state.model.photos ?? []).length) properties["_Artwork rights confirmed"] = "Yes";
    Object.assign(properties, {
      "_customily-thumb-id": `_thumb-id-${Date.now()}`,
      "_customily-preview": thumbUrl,
      "_customily-thumb": thumbUrl,
      "_customily-customization-url": editUrl(`customily-id-${id}`),
      "_customily-production-url": printUrl,
      "_customily-personalization-id": `_customily-id-${id}`,
    });
    if (state.saveOnly) return finishSave(`customily-id-${id}`, variant, properties, null);
    say("Adding to your cart…");
    const bundle = { sections: [] };
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: bundle }));
    window.PBPreviewTag?.(properties); // checkout picture: see assets/pb.js
    const add = await fetch(`${window.Shopify?.routes?.root ?? "/"}cart/add.js`, {
      method: "POST",
      headers: { "content-type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({
        items: [{ id: variant.id, quantity: state.qty, properties }],
        ...(bundle.sections.length ? { sections: bundle.sections.join(","), sections_url: location.pathname } : {}),
      }),
    });
    const added = await add.json().catch(() => ({}));
    if (!add.ok) {
      throw new Error(add.status === 429 ? "the store is busy right now, so wait a few seconds" : added.description || added.message || "we couldn't add it to your cart");
    }
    saveDesign(`customily-id-${id}`, null);
    remember(`customily-id-${id}`, variant, properties);
    askWho(`customily-id-${id}`);
    const replaced = await replaceEdited();
    await announceCart(added, btn);
    say(replaced ? "Added to your cart, replacing the earlier version." : "Added to your cart.");
  }

  /**
   * Full sheet: no studio render. The print file is the shopper's repeat at
   * 6000 px, hosted with the preview and the original; the six Customily keys
   * point at them the way AI prints do.
   */
  async function addSheetToCart(btn) {
    const tile = Object.values(state.photos)[0];
    const u = state.upload;
    const variant = currentVariant();
    const id = uuid();
    say("Preparing your 6000 px print file…");
    const print = await sheetPrintJpeg(tile, (f) => say(`Preparing your 6000 px print file… ${Math.round(f * 100)}%`));
    const thumbC = document.createElement("canvas");
    thumbC.width = thumbC.height = 800;
    drawSheet(thumbC.getContext("2d"), tile, 800);
    const thumb = await new Promise((res) => thumbC.toBlob(res, "image/jpeg", 0.85));
    say("Uploading your print file…");
    const ext = /png/.test(u.file.type) ? "png" : /webp/.test(u.file.type) ? "webp" : "jpg";
    const [printUrl, thumbUrl, originalUrl] = await Promise.all([
      hostSheetFile(`sheet-prod-${id}.jpg`, print),
      hostSheetFile(`sheet-thumb-${id}.jpg`, thumb),
      u.file.size < 45e6 ? hostSheetFile(`sheet-original-${id}.${ext}`, u.file, u.file.type || "image/jpeg").catch(() => null) : null,
    ]);
    const properties = {};
    const arrives = arrivesBy();
    if (arrives) properties["Arrives by"] = arrives;
    for (const [k, v] of appProperties()) properties[k] = v;
    for (const [k, v] of visibleProperties()) properties[k] = v;
    if (cfg.uploadKey) properties[cfg.uploadKey] = originalUrl || printUrl;
    properties["Artwork Fit"] = (tile.sheetFit || "cover") === "cover" ? "Crop to fill" : "Fit whole image";
    properties["_Artwork rights confirmed"] = "Yes";
    Object.assign(properties, {
      "_customily-thumb-id": `_thumb-id-${Date.now()}`,
      "_customily-preview": thumbUrl,
      "_customily-thumb": thumbUrl,
      "_customily-customization-url": editUrl(`customily-id-${id}`),
      "_customily-production-url": printUrl,
      "_customily-personalization-id": `_customily-id-${id}`,
    });
    if (state.saveOnly) return finishSave(`customily-id-${id}`, variant, properties, originalUrl || printUrl);
    say("Adding to your cart…");
    const bundle = { sections: [] };
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: bundle }));
    window.PBPreviewTag?.(properties); // checkout picture: see assets/pb.js
    const add = await fetch(`${window.Shopify?.routes?.root ?? "/"}cart/add.js`, {
      method: "POST",
      headers: { "content-type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({
        items: [{ id: variant.id, quantity: state.qty, properties }],
        ...(bundle.sections.length ? { sections: bundle.sections.join(","), sections_url: location.pathname } : {}),
      }),
    });
    const added = await add.json().catch(() => ({}));
    if (!add.ok) {
      throw new Error(add.status === 429 ? "the store is busy right now, so wait a few seconds" : added.description || added.message || "we couldn't add it to your cart");
    }
    saveDesign(`customily-id-${id}`, originalUrl || printUrl);
    remember(`customily-id-${id}`, variant, properties);
    askWho(`customily-id-${id}`);
    const replaced = await replaceEdited();
    await announceCart(added, btn);
    say(replaced ? "Added to your cart, replacing the earlier version." : "Added to your cart.");
  }

  /* ----- Edit design: the cart line reopens this exact design (same device) ----- */

  const EDITS_KEY = "pbcz:designs";
  const EDITS_MAX = 30;

  /** The cart line's "Edit design" link: this product, carrying the design's id. */
  function editUrl(pid) {
    return `${location.origin}${window.Shopify?.routes?.root ?? "/"}products/${layout().handle}?pbedit=${encodeURIComponent(pid)}${cfg.dream && dreamId ? `&pbdream=${dreamId}` : ""}`;
  }

  function readEdits() {
    try {
      return JSON.parse(localStorage.getItem(EDITS_KEY) || "[]");
    } catch {
      return [];
    }
  }

  /** Everything needed to rebuild the design: words, fonts, colours, layout, roll, hosted photos and crops. */
  function saveDesign(pid, uploadUrl) {
    try {
      const photos = {};
      if (cfg.mode !== "upload") {
        for (const [key, p] of Object.entries(state.photos)) {
          if (p.hosted?.url) photos[key] = { src: p.hosted.url, derived: !!p.derived, cut: p.cut ?? null };
        }
      }
      const tile = cfg.mode === "upload" ? Object.values(state.photos)[0] : null;
      const record = {
        id: pid,
        ts: Date.now(),
        layoutId: layout().id,
        handle: cfg.handle,
        size: state.size,
        qty: state.qty,
        values: state.values,
        fonts: Object.fromEntries(Object.entries(state.fonts).filter(([k]) => !k.startsWith("__"))),
        colors: state.colors,
        tints: state.tints,
        choices: state.choices,
        unlinked: [...(state.unlinked ?? [])],
        rights: !!state.rights,
        photos,
        upload:
          tile && state.upload && uploadUrl
            ? {
                src: uploadUrl,
                name: state.upload.file?.name || "artwork",
                type: state.upload.file?.type || "image/png",
                tile: Object.fromEntries(
                  ["fit", "sheetFit", "zoom", "panX", "panY", "rot", "quarter", "tiltDeg", "cut", "text"].map((k) => [k, tile[k] ?? null]),
                ),
              }
            : null,
      };
      lastRecord = record;
      const list = readEdits().filter((r) => r.id !== pid);
      list.unshift(record);
      localStorage.setItem(EDITS_KEY, JSON.stringify(list.slice(0, EDITS_MAX)));
    } catch {
      /* private mode or full storage: the link just opens the product */
    }
  }

  /* ----- saved designs: kept on the shopper's account (relay api/saved.js), so they can reorder or edit later ----- */

  let lastRecord = null;
  const PENDING_KEY = "pbcz:pending-saves";
  /** Proof of which customer this is, signed by Liquid (sections/pb-account-sig.liquid); null when signed out. */
  async function accountWho() {
    if (!cfg.customerId) return null;
    const r = await fetch(`${window.Shopify?.routes?.root ?? "/"}?sections=pb-account-sig&_=${Date.now()}`, { credentials: "same-origin" });
    const j = await r.json();
    const m = /data-pb-as>([\s\S]*?)<\/script>/.exec(j["pb-account-sig"] || "");
    return m ? JSON.parse(m[1]) : null;
  }
  async function accountCall(body) {
    const who = await accountWho();
    if (!who) return null;
    const r = await fetch(`${api}/api/saved`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ ...body, who }) });
    return r.json().catch(() => null);
  }
  /**
   * Keeps a finished design: what an add to cart sends (variant + line properties, print file link included)
   * plus the record that rebuilds it in the customizer. Signed in, it goes to their account; signed out, it
   * waits on this device and is added the next time they are signed in (here or on their account page).
   */
  async function remember(pid, variant, properties) {
    const design = {
      id: pid, variantId: variant.id, handle: layout().handle, size: variant.title,
      title: root.querySelector(".pbcz__title")?.textContent.trim() || state.model.storeTitle || state.model.name,
      thumb: properties["_customily-preview"] || "", properties, editUrl: properties["_customily-customization-url"] || "",
      edit: lastRecord?.id === pid ? lastRecord : null,
    };
    if (cfg.customerId) {
      const r = await accountCall({ action: "save", design, source: state.saveOnly ? "save" : "cart" }).catch(() => null);
      if (r?.ok) return "account";
    }
    try {
      const list = JSON.parse(localStorage.getItem(PENDING_KEY) || "[]").filter((d) => d.id !== pid);
      list.unshift(design);
      localStorage.setItem(PENDING_KEY, JSON.stringify(list.slice(0, 10)));
    } catch { /* private mode */ }
    return "device";
  }
  /** Designs saved while signed out join the account as soon as there is one. */
  async function flushPending() {
    if (!cfg.customerId) return;
    let list = [];
    try { list = JSON.parse(localStorage.getItem(PENDING_KEY) || "[]"); } catch { /* nothing waiting */ }
    for (const design of list.reverse()) await accountCall({ action: "save", design }).catch(() => null);
    if (list.length) try { localStorage.removeItem(PENDING_KEY); } catch { /* private mode */ }
  }
  /**
   * ?pbfill={...}: an earlier order's visible details (from the account page, when its print file is no longer
   * stored). Words go back into the fields with the same names, colours are matched by their names. Best effort:
   * the shopper checks the preview before adding it again.
   */
  function applyFill(props) {
    const norm = (v) => String(v ?? "").replace(/[▼▾]/g, "").trim().toLowerCase();
    const want = Object.fromEntries(Object.entries(props).map(([k, v]) => [norm(k), String(v)]));
    let hits = 0;
    for (const f of state.model.fields ?? []) {
      const v = want[norm(f.key)] ?? want[norm(plainLabel(f.label))];
      if (v != null) { state.values[f.key] = v.slice(0, f.maxLength || 60); hits += 1; }
    }
    for (const layer of state.model.layers) {
      if (layer.kind !== "vector" || !layer.colorOptions?.length) continue;
      const hex = layer.colorOptions.find((o) => norm(o.name) === want[norm(layer.name)])?.hex;
      if (hex) { state.tints[layer.uuid] = { "*": hex }; hits += 1; }
    }
    const ink = want.color ?? want["text color"] ?? want["font color"];
    if (ink) for (const f of state.model.fields ?? []) {
      const hex = (f.colorOptions ?? []).find((o) => norm(o.name) === norm(ink))?.hex;
      if (hex) { state.colors[f.key] = hex; hits += 1; }
    }
    return hits;
  }

  /* ----- People: who is this print for? (relay api/people.js) ----- */

  const RELATIONS = [["", "Relationship (optional)"], ["partner", "Partner"], ["mom", "Mom"], ["dad", "Dad"], ["son", "Son"], ["daughter", "Daughter"], ["brother", "Brother"], ["sister", "Sister"], ["grandparent", "Grandparent"], ["grandchild", "Grandchild"], ["friend", "Friend"], ["coworker", "Coworker"], ["client", "Client"], ["other", "Someone else"]];
  const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const WHO_PENDING = "pbcz:pending-people";
  async function peopleCall(body) {
    const who = await accountWho();
    if (!who) return null;
    const r = await fetch(`${api}/api/people`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ ...body, who }) });
    return r.json().catch(() => null);
  }
  /** A name typed on the print (or handed over by a "Make a print for Ben" link), if there is one. */
  function guessName() {
    const fromLink = new URLSearchParams(location.search).get("pbfor");
    if (fromLink) return fromLink.trim().slice(0, 40);
    for (const f of state.model.fields ?? []) {
      const v = String(state.values[f.key] || "").trim();
      const sample = cfg.textDefaults?.[state.model.id]?.[f.key] ?? state.model.textDefaults?.[f.key] ?? cfg.sampleText?.Name;
      if (!v || v === sample || v.length > 24 || !/name|text|line|phrase|initial/i.test(`${f.key} ${f.label}`)) continue;
      if (/^[\p{L}][\p{L}'. -]*$/u.test(v) && !/\b(merry|happy|christmas|birthday|love|from|xoxo|santa|holidays?)\b/i.test(v)) return v;
    }
    return "";
  }
  /**
   * After a design is saved or added to the cart: one small question. A name we already know is linked
   * without asking; "Not now" is remembered for the rest of the visit.
   */
  async function askWho(pid) {
    const box = $("[data-pb-cz-who]");
    if (!box) return;
    try { if (sessionStorage.getItem("pbcz:who-snooze") || sessionStorage.getItem(`pbcz:who:${pid}`)) return; } catch { /* private mode: just ask */ }
    const name = guessName();
    const known = cfg.customerId ? (await peopleCall({ action: "list" }).catch(() => null))?.people ?? [] : [];
    const match = name && known.find((p) => p.name.toLowerCase() === name.toLowerCase());
    const done = (text) => { box.replaceChildren(el("p", { className: "pbcz-who__done", textContent: text })); box.hidden = false; try { sessionStorage.setItem(`pbcz:who:${pid}`, "1"); } catch { /* fine */ } };
    if (match) {
      const r = await peopleCall({ action: "save", person: { id: match.id, name: match.name }, print: pid }).catch(() => null);
      if (r?.ok) done(`Added to ${match.name}'s prints.`);
      return;
    }
    const nameIn = el("input", { type: "text", className: "pbcz-input pbcz-input--sm", placeholder: "Their name", value: name, maxLength: 40 });
    nameIn.setAttribute("aria-label", "Their name");
    const rel = el("select", { className: "pbcz-who__sel" }, RELATIONS.map(([v, l]) => el("option", { value: v, textContent: l })));
    rel.setAttribute("aria-label", "Relationship");
    const month = el("select", { className: "pbcz-who__sel" }, [el("option", { value: "", textContent: "Birthday month" }), ...MONTHS.map((m, i) => el("option", { value: String(i + 1).padStart(2, "0"), textContent: m }))]);
    month.setAttribute("aria-label", "Birthday month");
    const day = el("select", { className: "pbcz-who__sel" }, [el("option", { value: "", textContent: "Day" }), ...Array.from({ length: 31 }, (_, i) => el("option", { value: String(i + 1).padStart(2, "0"), textContent: String(i + 1) }))]);
    day.setAttribute("aria-label", "Birthday day");
    const save = el("button", { type: "button", className: "pbcz-pill pbcz-pill--sm", textContent: "Save" });
    const skip = el("button", { type: "button", className: "pbcz-who__skip", textContent: "Not now" });
    skip.addEventListener("click", () => { box.hidden = true; try { sessionStorage.setItem("pbcz:who-snooze", "1"); } catch { /* fine */ } });
    save.addEventListener("click", async () => {
      const person = { name: nameIn.value.trim(), relation: rel.value, birthday: month.value && day.value ? `${month.value}-${day.value}` : undefined };
      if (!person.name) { nameIn.focus(); return; }
      save.disabled = true;
      const r = cfg.customerId ? await peopleCall({ action: "save", person, print: pid }).catch(() => null) : null;
      if (r?.ok) return done(`Saved. ${person.name}'s prints are together in your account.`);
      // Signed out (or the save didn't go through): keep it on this device until they sign in.
      try {
        const list = JSON.parse(localStorage.getItem(WHO_PENDING) || "[]");
        list.push({ person, print: pid });
        localStorage.setItem(WHO_PENDING, JSON.stringify(list.slice(-10)));
      } catch { /* private mode */ }
      done(`Noted. Sign in and ${person.name} is added to your account.`);
    });
    box.replaceChildren(
      el("p", { className: "pbcz-who__h", textContent: "Who's this one for?" }),
      el("p", { className: "pbcz-who__sub", textContent: "We'll keep their prints together and can remind you before their birthday." }),
      el("div", { className: "pbcz-who__row" }, [nameIn, rel]),
      el("div", { className: "pbcz-who__row" }, [month, day]),
      el("div", { className: "pbcz-who__row" }, [save, skip]),
    );
    box.hidden = false;
  }
  async function flushPendingPeople() {
    if (!cfg.customerId) return;
    let list = [];
    try { list = JSON.parse(localStorage.getItem(WHO_PENDING) || "[]"); } catch { /* nothing waiting */ }
    for (const x of list) await peopleCall({ action: "save", person: x.person, print: x.print }).catch(() => null);
    if (list.length) try { localStorage.removeItem(WHO_PENDING); } catch { /* private mode */ }
  }

  /** "Save to my account": everything an add to cart does (print file and all), without touching the cart. */
  async function finishSave(pid, variant, properties, uploadUrl) {
    saveDesign(pid, uploadUrl);
    const where = await remember(pid, variant, properties);
    const status = $("[data-pb-cz-status]");
    if (where === "account") {
      say("Saved to your account. ");
      status.append(el("a", { href: cfg.accountUrl || "/account", textContent: "See your prints" }));
    } else {
      say("Saved on this device. ");
      status.append(el("a", { href: `${cfg.loginUrl || "/account/login"}?return_url=${encodeURIComponent(location.pathname + location.search)}`, textContent: "Sign in to keep it in your account" }));
    }
    askWho(pid);
  }

  /** Studio files load through the relay, which allows this store's origin (canvas-safe). */
  function loadRemote(url) {
    const src = String(url).replace(/^https:\/\/[^/]*modal\.run(?=\/files\/)/, api);
    return fetch(src, { mode: "cors" })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(r.status))))
      .then(async (blob) => {
        const img = new Image();
        img.src = URL.createObjectURL(blob);
        await img.decode();
        return { img, blob };
      });
  }

  /** ?pbedit=<id>: put back the saved design, if this device has it. */
  async function restoreDesign(edit) {
    Object.assign(state.values, edit.values ?? {});
    Object.assign(state.fonts, edit.fonts ?? {});
    Object.assign(state.colors, edit.colors ?? {});
    state.tints = { ...state.tints, ...(edit.tints ?? {}) };
    state.choices = { ...(edit.choices ?? {}) };
    state.unlinked = new Set(edit.unlinked ?? []);
    if (edit.size) state.size = edit.size;
    if (edit.qty) state.qty = edit.qty;
    await Promise.all(Object.values(state.fonts).map((f) => loadFont(f)));
    for (const [key, rec] of Object.entries(edit.photos ?? {})) {
      const { img } = await loadRemote(rec.src);
      const entry = { image: img, zoom: 1, panX: 0, panY: 0, derived: rec.derived, cut: rec.cut, restored: true };
      state.photos[key] = entry;
      await bake(entry, key);
      // Already hosted: keep the URL instead of uploading it again.
      clearTimeout(entry.hostTimer);
      entry.hosted = { rev: entry.rev, job: Promise.resolve(rec.src), url: rec.src };
    }
    const u = edit.upload;
    if (u) {
      const { img, blob } = await loadRemote(u.src);
      const file = new File([blob], u.name, { type: u.type || blob.type });
      state.upload = { file, image: img, width: img.naturalWidth, height: img.naturalHeight, url: u.src };
      const tile = { image: img, source: img, upload: state.upload, zoom: 1, panX: 0, panY: 0, rot: 0 };
      for (const [k, v] of Object.entries(u.tile ?? {})) if (v != null) tile[k] = v;
      if (tile.cut?.on) tile.image = cutout(img, tile.cut.tol, tile.cut.mode);
      for (const f of state.model.photos ?? []) state.photos[f.key] = tile;
      const key0 = state.model.photos?.[0]?.key;
      if (key0) await bake(tile, key0);
    }
    state.editing = edit.id;
    if (edit.rights) state.rights = true;
  }

  /** Re-adding an edited design takes the old cart line's place (when they leave the box ticked). */
  async function replaceEdited() {
    const box = root.querySelector("[data-pb-cz-replace] input");
    if (!state.editing || !box?.checked) return false;
    try {
      const cart = await (await fetch(`${window.Shopify?.routes?.root ?? "/"}cart.js`)).json();
      const old = cart.items.find((it) => it.properties?.["_customily-personalization-id"] === `_${state.editing}`);
      if (!old) return false;
      await fetch(`${window.Shopify?.routes?.root ?? "/"}cart/change.js`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ id: old.key, quantity: 0 }),
      });
      return true;
    } catch {
      return false;
    }
  }

  /** While editing a design that's in the cart: "Replace the earlier version" (ticked). */
  async function offerReplace() {
    try {
      const cart = await (await fetch(`${window.Shopify?.routes?.root ?? "/"}cart.js`)).json();
      if (!cart.items.some((it) => it.properties?.["_customily-personalization-id"] === `_${state.editing}`)) return;
      const status = $("[data-pb-cz-status]");
      const label = el("label", { className: "pbcz__rights pbcz__rights--inline pbcz-replace" }, [
        el("input", { type: "checkbox", checked: true }),
        el("span", { textContent: "Replace the earlier version in my cart" }),
      ]);
      label.dataset.pbCzReplace = "";
      status?.before(label);
    } catch {
      /* no offer, a plain add */
    }
  }

  /** "Sep 28 - Oct 2": today plus the configured business days, skipping weekends. */
  function arrivesBy() {
    if (!Array.isArray(cfg.arrives)) return null;
    const [lo, hi] = cfg.arrives.map(Number);
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi < lo) return null;
    const add = (n) => {
      const d = new Date();
      while (n > 0) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) n -= 1;
      }
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    };
    return `${add(lo)} - ${add(hi)}`;
  }

  /** Properties app blocks put in the section's form. */
  function appProperties() {
    const form = root.querySelector("[data-pb-cz-form]");
    if (!form) return [];
    const out = [];
    for (const [name, value] of new FormData(form)) {
      const m = /^properties\[(.+)\]$/.exec(name);
      if (m && typeof value === "string" && value) out.push([m[1], value]);
    }
    return out;
  }

  /**
   * Opens the cart drawer. Uses the Paperbloom drawer's API (assets/pb.js) when
   * it is on the page, otherwise Impact's own events, the way its form does.
   */
  async function announceCart(added, source) {
    const base = window.Shopify?.routes?.root ?? "/";
    const cart = await (await fetch(`${base}cart.js`)).json();
    cart.sections = added.sections;
    const items = added.items ?? [added];
    if (window.PB?.cart?.open) {
      await window.PB.cart.open();
      // baseEvent 'pb' stops the Paperbloom drawer re-rendering a second time.
      document.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "pb", cart } }));
    } else {
      (source || document.documentElement).dispatchEvent(
        new CustomEvent("variant:add", { bubbles: true, detail: { items, cart } }),
      );
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "variant:add", cart } }),
      );
      if (!added.sections) document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    }
    document.dispatchEvent(new CustomEvent("pb:cart:added", { detail: { items, cart } }));
  }

  function say(text, isError = false) {
    status.textContent = text;
    status.classList.toggle("is-error", isError);
  }

  /* ----- wiring ----- */

  for (const b of root.querySelectorAll("[data-pb-cz-add]")) b.addEventListener("click", () => addToCart(b));
  const saveBtn = $("[data-pb-cz-save]");
  saveBtn?.addEventListener("click", async () => {
    state.saveOnly = true;
    saveBtn.disabled = true;
    try { await addToCart(saveBtn); } finally { state.saveOnly = false; saveBtn.disabled = false; }
  });
  flushPending();
  flushPendingPeople();

  const qtyVal = $("[data-pb-cz-qty-val]");
  for (const [sel, d] of [["[data-pb-cz-qty-dec]", -1], ["[data-pb-cz-qty-inc]", 1]]) {
    $(sel)?.addEventListener("click", () => {
      state.qty = Math.max(1, Math.min(99, state.qty + d));
      if (qtyVal) qtyVal.textContent = String(state.qty);
    });
  }

  // Gallery: the live preview is the first slide, product mockups follow.
  for (const t of root.querySelectorAll("[data-pb-cz-thumb]")) {
    t.addEventListener("click", () => {
      for (const x of root.querySelectorAll("[data-pb-cz-thumb]")) x.classList.toggle("is-on", x === t);
      if (!t.dataset.pbScene || !live) {
        stageBox.classList.remove("is-livemock");
        if (liveStage) window.PBLive?.remove(liveStage);
      }
      // A product with no photos yet (Dream It, a fresh builder draft) still has its live scenes.
      const scene = t.dataset.pbScene && live && liveStage;
      if (!t.dataset.src && !scene) {
        stageBox.classList.remove("is-photo");
        return;
      }
      if (t.dataset.src) {
        photo.src = t.dataset.src;
        photo.alt = t.dataset.alt || "";
      } else {
        photo.removeAttribute("src");
      }
      stageBox.classList.add("is-photo");
      // Scene thumbs: the stage shows the live mockup (static photo until its first frame).
      if (t.dataset.pbScene && live && liveStage) {
        window.PBLive.add(liveStage, t.dataset.pbScene);
        stageBox.classList.add("is-livemock");
        window.PBLive.update();
      }
    });
  }
  const rights = $("[data-pb-cz-rights]");
  if (rights) rights.addEventListener("change", () => (state.rights = rights.checked));
  $("[data-pb-cz-zoom]")?.addEventListener("click", (e) => {
    state.zoomed = !state.zoomed;
    stageBox.classList.toggle("is-zoomed", state.zoomed);
    e.currentTarget.setAttribute("aria-pressed", String(state.zoomed));
    e.currentTarget.setAttribute("aria-label", state.zoomed ? "Zoom out" : "Zoom in");
    schedule();
  });
  let lastWidth = stageBox.clientWidth;
  new ResizeObserver(() => {
    if (Math.abs(stageBox.clientWidth - lastWidth) < 40) return;
    lastWidth = stageBox.clientWidth;
    schedule();
  }).observe(stageBox);

  // Mobile: the sticky bar only shows once the in-page button scrolls away.
  const bar = $("[data-pb-cz-bar]");
  const mainBtn = $("[data-pb-cz-add-main]");
  if (bar && mainBtn) {
    new IntersectionObserver(([e]) => bar.classList.toggle("is-shown", !e.isIntersecting && e.boundingClientRect.top > 0)).observe(mainBtn);
  }

  // The skeleton shows at once; if the studio is slow to answer, say why.
  const warming = $("[data-pb-cz-warming]");
  const warmTimer = setTimeout(() => {
    if (!firstPaint && warming) warming.hidden = false;
  }, 3000);
  const editId = new URLSearchParams(location.search).get("pbedit");
  let edit = editId ? readEdits().find((r) => r.id === editId) : null;
  // Not on this device: a signed-in shopper's saved designs carry the same record.
  if (editId && !edit && cfg.customerId) {
    edit = (await accountCall({ action: "list" }).catch(() => null))?.designs?.find((d) => d.id === editId)?.edit ?? null;
  }
  if (edit) {
    const i = layouts.findIndex((l) => l.id === edit.layoutId);
    if (i >= 0) state.layoutIndex = i;
  }
  try {
    await openLayout(state.layoutIndex);
    const fill = !edit && new URLSearchParams(location.search).get("pbfill");
    if (fill) {
      try {
        if (applyFill(JSON.parse(fill))) {
          buildControls();
          schedule();
          setTimeout(() => say("We filled in the choices from your earlier order. Check it over, then add it to your cart."), 50);
        }
      } catch (err) {
        console.error("[pb-customizer] fill", err);
      }
    }
    if (edit) {
      try {
        await restoreDesign(edit);
        buildControls();
        // Rights boxes (upload products' in the section, photo templates' in the step) come back ticked.
        if (state.rights) for (const box of root.querySelectorAll("[data-pb-cz-rights], .pbcz-photos .pbcz__rights--inline:not(.pbcz-cuttoggle) input")) box.checked = true;
        schedule();
        say("Your design is back. Change anything, then add it to your cart again.");
        offerReplace();
      } catch (err) {
        console.error("[pb-customizer] restore", err);
      }
    }
    clearTimeout(warmTimer);
    if (warming) warming.hidden = true;
    if (!edit) say("");
  } catch (err) {
    console.error("[pb-customizer]", err);
    clearTimeout(warmTimer);
    if (warming) warming.hidden = true;
    stageBox.classList.add("is-failed");
    say("The live preview couldn't load. Please refresh the page to try again.", true);
    return;
  }
  // Warm the other layout, photo masks and every font in the background.
  idle(() => {
    warmMasks();
    for (const l of layouts) if (!state.models[l.id]) fetchModel(api, l.id).then((m) => (state.models[l.id] = m)).catch(() => {});
    // Only the fonts in use load up front; chip previews load as they're seen.
  });
}

/* ---------- strip JPEG encoder (baseline, 4:2:0, JFIF 300 dpi) ---------- */

/**
 * Encodes a width x height JPEG without ever holding it on one canvas: the
 * caller draws each 256-row strip (draw(ctx, y0, rows), offset so y0 is the
 * strip's top) and the strips are encoded as they come. For browsers whose
 * canvas limit is below a 6000 px print (iOS Safari: ~16.7M px).
 */
async function stripJpeg(width, height, quality, draw, onProgress) {
  const ZZ = [0, 1, 8, 16, 9, 2, 3, 10, 17, 24, 32, 25, 18, 11, 4, 5, 12, 19, 26, 33, 40, 48, 41, 34, 27, 20, 13, 6, 7, 14, 21, 28, 35, 42, 49, 56, 57, 50, 43, 36, 29, 22, 15, 23, 30, 37, 44, 51, 58, 59, 52, 45, 38, 31, 39, 46, 53, 60, 61, 54, 47, 55, 62, 63];
  const QY = [16, 11, 10, 16, 24, 40, 51, 61, 12, 12, 14, 19, 26, 58, 60, 55, 14, 13, 16, 24, 40, 57, 69, 56, 14, 17, 22, 29, 51, 87, 80, 62, 18, 22, 37, 56, 68, 109, 103, 77, 24, 35, 55, 64, 81, 104, 113, 92, 49, 64, 78, 87, 103, 121, 120, 101, 72, 92, 95, 98, 112, 100, 103, 99];
  const QC = [17, 18, 24, 47, 99, 99, 99, 99, 18, 21, 26, 66, 99, 99, 99, 99, 24, 26, 56, 99, 99, 99, 99, 99, 47, 66, 99, 99, 99, 99, 99, 99, ...new Array(32).fill(99)];
  const q = Math.max(1, Math.min(100, Math.round(quality * 100)));
  const scale = q < 50 ? 5000 / q : 200 - 2 * q;
  const qt = (base) => base.map((v) => Math.max(1, Math.min(255, Math.floor((v * scale + 50) / 100))));
  const qy = qt(QY);
  const qc = qt(QC);
  const HT = {
    dcY: [[0, 1, 5, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0], [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]],
    dcC: [[0, 3, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0], [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]],
    acY: [[0, 2, 1, 3, 3, 2, 4, 3, 5, 5, 4, 4, 0, 0, 1, 0x7d], [0x01, 0x02, 0x03, 0x00, 0x04, 0x11, 0x05, 0x12, 0x21, 0x31, 0x41, 0x06, 0x13, 0x51, 0x61, 0x07, 0x22, 0x71, 0x14, 0x32, 0x81, 0x91, 0xa1, 0x08, 0x23, 0x42, 0xb1, 0xc1, 0x15, 0x52, 0xd1, 0xf0, 0x24, 0x33, 0x62, 0x72, 0x82, 0x09, 0x0a, 0x16, 0x17, 0x18, 0x19, 0x1a, 0x25, 0x26, 0x27, 0x28, 0x29, 0x2a, 0x34, 0x35, 0x36, 0x37, 0x38, 0x39, 0x3a, 0x43, 0x44, 0x45, 0x46, 0x47, 0x48, 0x49, 0x4a, 0x53, 0x54, 0x55, 0x56, 0x57, 0x58, 0x59, 0x5a, 0x63, 0x64, 0x65, 0x66, 0x67, 0x68, 0x69, 0x6a, 0x73, 0x74, 0x75, 0x76, 0x77, 0x78, 0x79, 0x7a, 0x83, 0x84, 0x85, 0x86, 0x87, 0x88, 0x89, 0x8a, 0x92, 0x93, 0x94, 0x95, 0x96, 0x97, 0x98, 0x99, 0x9a, 0xa2, 0xa3, 0xa4, 0xa5, 0xa6, 0xa7, 0xa8, 0xa9, 0xaa, 0xb2, 0xb3, 0xb4, 0xb5, 0xb6, 0xb7, 0xb8, 0xb9, 0xba, 0xc2, 0xc3, 0xc4, 0xc5, 0xc6, 0xc7, 0xc8, 0xc9, 0xca, 0xd2, 0xd3, 0xd4, 0xd5, 0xd6, 0xd7, 0xd8, 0xd9, 0xda, 0xe1, 0xe2, 0xe3, 0xe4, 0xe5, 0xe6, 0xe7, 0xe8, 0xe9, 0xea, 0xf1, 0xf2, 0xf3, 0xf4, 0xf5, 0xf6, 0xf7, 0xf8, 0xf9, 0xfa]],
    acC: [[0, 2, 1, 2, 4, 4, 3, 4, 7, 5, 4, 4, 0, 1, 2, 0x77], [0x00, 0x01, 0x02, 0x03, 0x11, 0x04, 0x05, 0x21, 0x31, 0x06, 0x12, 0x41, 0x51, 0x07, 0x61, 0x71, 0x13, 0x22, 0x32, 0x81, 0x08, 0x14, 0x42, 0x91, 0xa1, 0xb1, 0xc1, 0x09, 0x23, 0x33, 0x52, 0xf0, 0x15, 0x62, 0x72, 0xd1, 0x0a, 0x16, 0x24, 0x34, 0xe1, 0x25, 0xf1, 0x17, 0x18, 0x19, 0x1a, 0x26, 0x27, 0x28, 0x29, 0x2a, 0x35, 0x36, 0x37, 0x38, 0x39, 0x3a, 0x43, 0x44, 0x45, 0x46, 0x47, 0x48, 0x49, 0x4a, 0x53, 0x54, 0x55, 0x56, 0x57, 0x58, 0x59, 0x5a, 0x63, 0x64, 0x65, 0x66, 0x67, 0x68, 0x69, 0x6a, 0x73, 0x74, 0x75, 0x76, 0x77, 0x78, 0x79, 0x7a, 0x82, 0x83, 0x84, 0x85, 0x86, 0x87, 0x88, 0x89, 0x8a, 0x92, 0x93, 0x94, 0x95, 0x96, 0x97, 0x98, 0x99, 0x9a, 0xa2, 0xa3, 0xa4, 0xa5, 0xa6, 0xa7, 0xa8, 0xa9, 0xaa, 0xb2, 0xb3, 0xb4, 0xb5, 0xb6, 0xb7, 0xb8, 0xb9, 0xba, 0xc2, 0xc3, 0xc4, 0xc5, 0xc6, 0xc7, 0xc8, 0xc9, 0xca, 0xd2, 0xd3, 0xd4, 0xd5, 0xd6, 0xd7, 0xd8, 0xd9, 0xda, 0xe2, 0xe3, 0xe4, 0xe5, 0xe6, 0xe7, 0xe8, 0xe9, 0xea, 0xf2, 0xf3, 0xf4, 0xf5, 0xf6, 0xf7, 0xf8, 0xf9, 0xfa]],
  };
  // Canonical Huffman codes: value -> [code, length].
  const codes = ([bits, vals]) => {
    const out = new Array(256);
    let code = 0;
    let k = 0;
    for (let len = 1; len <= 16; len += 1) {
      for (let i = 0; i < bits[len - 1]; i += 1) out[vals[k++]] = [code++, len];
      code <<= 1;
    }
    return out;
  };
  const cDcY = codes(HT.dcY);
  const cDcC = codes(HT.dcC);
  const cAcY = codes(HT.acY);
  const cAcC = codes(HT.acC);

  // Output: 1MB chunks.
  const chunks = [];
  let buf = new Uint8Array(1 << 20);
  let pos = 0;
  const byte = (b) => {
    if (pos === buf.length) {
      chunks.push(buf);
      buf = new Uint8Array(1 << 20);
      pos = 0;
    }
    buf[pos++] = b;
  };
  const word = (w) => {
    byte((w >> 8) & 255);
    byte(w & 255);
  };
  let bitBuf = 0;
  let bitCnt = 0;
  const bits = (code, len) => {
    for (let i = len - 1; i >= 0; i -= 1) {
      bitBuf = (bitBuf << 1) | ((code >> i) & 1);
      bitCnt += 1;
      if (bitCnt === 8) {
        byte(bitBuf);
        if (bitBuf === 0xff) byte(0);
        bitBuf = 0;
        bitCnt = 0;
      }
    }
  };

  // Headers.
  word(0xffd8);
  word(0xffe0);
  word(16);
  for (const c of "JFIF") byte(c.charCodeAt(0));
  byte(0);
  word(0x0101);
  byte(1);
  word(300);
  word(300);
  byte(0);
  byte(0);
  word(0xffdb);
  word(132);
  byte(0);
  for (let k = 0; k < 64; k += 1) byte(qy[ZZ[k]]);
  byte(1);
  for (let k = 0; k < 64; k += 1) byte(qc[ZZ[k]]);
  word(0xffc0);
  word(17);
  byte(8);
  word(height);
  word(width);
  byte(3);
  for (const [id, samp, tq] of [[1, 0x22, 0], [2, 0x11, 1], [3, 0x11, 1]]) {
    byte(id);
    byte(samp);
    byte(tq);
  }
  word(0xffc4);
  word(2 + 4 * 17 + 12 + 12 + 162 + 162);
  for (const [cls, t] of [[0x00, HT.dcY], [0x10, HT.acY], [0x01, HT.dcC], [0x11, HT.acC]]) {
    byte(cls);
    for (const b of t[0]) byte(b);
    for (const v of t[1]) byte(v);
  }
  word(0xffda);
  word(12);
  byte(3);
  for (const [id, tables] of [[1, 0x00], [2, 0x11], [3, 0x11]]) {
    byte(id);
    byte(tables);
  }
  byte(0);
  byte(63);
  byte(0);

  // Separable DCT: coef[u][v] = sum C[u][x] C[v][y] f[x][y].
  const C = new Float64Array(64);
  for (let u = 0; u < 8; u += 1) {
    for (let x = 0; x < 8; x += 1) C[u * 8 + x] = (u === 0 ? Math.SQRT1_2 : 1) * 0.5 * Math.cos(((2 * x + 1) * u * Math.PI) / 16);
  }
  const tmp = new Float64Array(64);
  const coef = new Int32Array(64);
  const catOf = (v) => {
    let a = v < 0 ? -v : v;
    let n = 0;
    while (a) {
      n += 1;
      a >>= 1;
    }
    return n;
  };
  const block = (f, qtab, prev, cDc, cAc) => {
    for (let y = 0; y < 8; y += 1) {
      for (let u = 0; u < 8; u += 1) {
        let s = 0;
        for (let x = 0; x < 8; x += 1) s += C[u * 8 + x] * f[y * 8 + x];
        tmp[y * 8 + u] = s;
      }
    }
    for (let u = 0; u < 8; u += 1) {
      for (let v = 0; v < 8; v += 1) {
        let s = 0;
        for (let y = 0; y < 8; y += 1) s += C[v * 8 + y] * tmp[y * 8 + u];
        coef[v * 8 + u] = Math.round(s / qtab[v * 8 + u]);
      }
    }
    const dc = coef[0];
    const diff = dc - prev;
    const dcat = catOf(diff);
    bits(...cDc[dcat]);
    if (dcat) bits(diff < 0 ? diff - 1 + (1 << dcat) : diff, dcat);
    let run = 0;
    for (let k = 1; k < 64; k += 1) {
      const v = coef[ZZ[k]];
      if (!v) {
        run += 1;
        continue;
      }
      while (run > 15) {
        bits(...cAc[0xf0]);
        run -= 16;
      }
      const cat = catOf(v);
      bits(...cAc[(run << 4) | cat]);
      bits(v < 0 ? v - 1 + (1 << cat) : v, cat);
      run = 0;
    }
    if (run) bits(...cAc[0]);
    return dc;
  };

  const STRIP = 256;
  const strip = document.createElement("canvas");
  strip.width = width;
  strip.height = STRIP;
  const sx = strip.getContext("2d", { willReadFrequently: true });
  const Cb = new Float64Array(64);
  const Cr = new Float64Array(64);
  const Yb = [new Float64Array(64), new Float64Array(64), new Float64Array(64), new Float64Array(64)];
  let pY = 0;
  let pCb = 0;
  let pCr = 0;
  for (let y0 = 0; y0 < height; y0 += STRIP) {
    const rows = Math.min(STRIP, height - y0);
    sx.setTransform(1, 0, 0, 1, 0, 0);
    sx.clearRect(0, 0, width, STRIP);
    draw(sx, y0, rows);
    const px = sx.getImageData(0, 0, width, STRIP).data;
    for (let my = 0; my < rows; my += 16) {
      for (let mx = 0; mx < width; mx += 16) {
        Cb.fill(0);
        Cr.fill(0);
        for (let yy = 0; yy < 16; yy += 1) {
          const ry = Math.min(my + yy, rows - 1);
          for (let xx = 0; xx < 16; xx += 1) {
            const rx = Math.min(mx + xx, width - 1);
            const i = (ry * width + rx) * 4;
            const r = px[i];
            const g = px[i + 1];
            const b = px[i + 2];
            Yb[(yy >> 3) * 2 + (xx >> 3)][(yy & 7) * 8 + (xx & 7)] = 0.299 * r + 0.587 * g + 0.114 * b - 128;
            const ci = (yy >> 1) * 8 + (xx >> 1);
            Cb[ci] += (-0.168736 * r - 0.331264 * g + 0.5 * b) / 4;
            Cr[ci] += (0.5 * r - 0.418688 * g - 0.081312 * b) / 4;
          }
        }
        for (let k = 0; k < 4; k += 1) pY = block(Yb[k], qy, pY, cDcY, cAcY);
        pCb = block(Cb, qc, pCb, cDcC, cAcC);
        pCr = block(Cr, qc, pCr, cDcC, cAcC);
      }
    }
    onProgress?.(Math.min(1, (y0 + rows) / height));
    await new Promise((r) => setTimeout(r));
  }
  // Pad the last byte with 1s, then EOI.
  if (bitCnt) bits((1 << (8 - bitCnt)) - 1, 8 - bitCnt);
  word(0xffd9);
  chunks.push(buf.subarray(0, pos));
  strip.width = strip.height = 1;

  return new Blob(chunks, { type: "image/jpeg" });
}
